// Field descriptions for the Agentic Logic I/O contract
// (src/functions/agentic-logic/types/). These strings ride the Zod schemas
// via .describe() and double as the API/MCP documentation source.
//
// Envelope-wide conventions (restated on the fields they govern):
//  - Ids are Shopify GIDs: `gid://shopify/<Kind>/<numeric id>`.
//  - ALL money is in integer minor units (e.g. cents) with an explicit
//    ISO-4217 currency field alongside — never floats, never implied currency.
//  - Timestamps are ISO-8601 UTC strings.

export const AgenticLogicSelectedOptionDescriptions = {
  _schema:
    "One selected product option (e.g. Size, Color) on a cart or order line.",
  name: "Option name as configured on the Shopify product (e.g. 'Size').",
  value: "Selected option value (e.g. 'Medium').",
} as const;

export const AgenticLogicCartLineFlagsDescriptions = {
  _schema:
    "Provenance flags for a cart line. All flags are required; nullable markers explicitly distinguish unknown provenance from false.",
  giftCard:
    "True when the line is a Shopify gift-card product, false when known not to be one, or null when the surface exposes no authoritative marker. NULL on every checkout and post-purchase line — neither surface exposes a gift-card marker, so a clause keyed on this flag is inert there.",
  syntheticGift:
    "True when the line was injected as a gift-with-purchase by a promotion, false when known not to be one, or null when the surface exposes no authoritative marker.",
  bundleChild:
    "True when the line is a child component of an expanded bundle, false when known not to be one, or null when the surface exposes no authoritative marker. NULL on every post-purchase line, and never `true` onsite — `/cart.js` does not list bundle component lines at all.",
  igUpsell:
    "True when the line was added by an Intelligems upsell, false when known not to be one, or null when the surface exposes no authoritative marker.",
} as const;

export const AgenticLogicCartLineDescriptions = {
  _schema: "One line in the visitor's current cart.",
  productId:
    "Shopify product GID (gid://shopify/Product/<n>). The bare numeric id the Shopify admin and most PIM exports show is accepted and promoted to GID form on the way in; anything that is neither is rejected.",
  variantId:
    "Shopify variant GID (gid://shopify/ProductVariant/<n>). The bare numeric id the Shopify admin and most PIM exports show is accepted and promoted to GID form on the way in; anything that is neither is rejected.",
  quantity: "Number of units on the line. Positive integer.",
  unitPrice:
    "Per-unit price actually in the cart, in integer minor units of the cart's currency.",
  lineTotal:
    "Line total (unit price times quantity, after line-level adjustments), in integer minor units of the cart's currency.",
  selectedOptions: "Selected product options for the line's variant.",
  isSubscriptionLine:
    "True when the line carries a selling plan (subscription), false for a one-time purchase.",
  flags:
    "Provenance flags (gift card, synthetic gift, bundle child, Intelligems upsell). Which flags a surface can actually answer differs per surface — see the request contract's per-surface caveats.",
  tags: "The line product's Shopify tags, hydrated ONLY when an evaluated rule targets on tags — absent otherwise. Absent means 'not looked up' and an empty array means 'looked up, none'; the two are never interchangeable, and a rule reading an absent value must fail loudly rather than treat it as no tags.",
  collections:
    "Shopify collection GIDs the line product belongs to, hydrated ONLY when an evaluated rule targets on collections — absent otherwise. Bare numeric collection ids are accepted and promoted to GID form on the way in. Same absent-vs-empty rule as `tags`.",
} as const;

export const AgenticLogicCartDataDescriptions = {
  _schema:
    "Snapshot of the visitor's current cart. An empty cart (zero lines) is a legal cart — distinct from cart: null, which means the surface has no cart at all (post-purchase).",
  currency: "ISO-4217 currency code all money fields in this cart are in.",
  subtotal:
    "Cart merchandise subtotal in integer minor units of `currency`, before shipping and taxes.",
  itemCount:
    "Total unit quantity across all lines (sum of line quantities) — units, not the count of distinct lines.",
  lines: "The cart lines.",
} as const;

export const AgenticLogicOrderLineDiscountAllocationDescriptions = {
  _schema: "One discount amount allocated to an order line.",
  amount:
    "Discount amount allocated to the line, in integer minor units of the order's currency.",
  title: "Discount title or code as shown on the order, when available.",
} as const;

export const AgenticLogicOrderLineDescriptions = {
  ...AgenticLogicCartLineDescriptions,
  _schema:
    "One line on the completed order. Extends the cart-line shape with catalog metadata that is available once the order exists.",
  unitPrice:
    "Per-unit price actually paid, in integer minor units of the order's currencyCode.",
  lineTotal:
    "Line total (unit price times quantity, after line-level adjustments), in integer minor units of the order's currencyCode.",
  title: "Product title at purchase time, when available.",
  vendor: "Product vendor at purchase time, when available.",
  productType: "Shopify product type at purchase time, when available.",
  sellingPlanId:
    "Shopify selling-plan GID (gid://shopify/SellingPlan/<n>) when the line was purchased on a subscription.",
  discountAllocations:
    "Line-level discount allocations applied to this line, when any.",
} as const;

export const AgenticLogicOrderDataDescriptions = {
  _schema:
    "The completed order a post-purchase surface is rendered against. First-class on the post-purchase surface only; null everywhere else. Deliberately carries NO customer field — the top-level request `customer` is the single customer reference.",
  orderId: "Shopify order GID (gid://shopify/Order/<n>).",
  checkoutToken:
    "Shopify checkout token for the checkout that produced this order, when available.",
  createdAt: "Order creation time as an ISO-8601 UTC timestamp.",
  currencyCode:
    "ISO-4217 shop currency code the order's money fields are denominated in.",
  presentmentCurrency:
    "ISO-4217 currency code the shopper saw at checkout, which may differ from `currencyCode`.",
  itemsSubtotal:
    "Merchandise subtotal after line-level discounts but before order-level discounts, shipping, and taxes. Integer minor units.",
  discountTotal:
    "Total order-level discount amount in integer minor units, when any.",
  shippingTotal: "Shipping charged in integer minor units, when known.",
  taxTotal: "Taxes charged in integer minor units, when known.",
  total: "What the shopper was actually charged, in integer minor units.",
  lines: "The purchased order lines.",
} as const;

export const AgenticLogicPageProductDescriptions = {
  _schema:
    "The product a PDP placement is rendered on. Populated for onsite product-page placements only; null everywhere else.",
  productId:
    "Shopify product GID of the product being viewed (gid://shopify/Product/<n>). The bare numeric id the Shopify admin shows is accepted and promoted to GID form on the way in; anything that is neither is rejected.",
  handle: "Shopify product handle (URL slug) of the product being viewed.",
} as const;

export const AgenticLogicCustomerAmountSpentDescriptions = {
  _schema: "Lifetime spend aggregate for the customer.",
  amount: "Lifetime amount spent in integer minor units of `currency`.",
  currency: "ISO-4217 currency code `amount` is denominated in.",
} as const;

export const AgenticLogicCustomerDataDescriptions = {
  _schema:
    "Minimal identified-customer context, or null for guests. PII-MINIMAL GUARANTEE: this object carries ids, counts, and money aggregates ONLY — no email, name, phone, or address field may ever be added here. That is a contract property, not a data-availability accident; richer profile data is fetched at rule runtime via sdk.getCustomer().",
  id: "Shopify customer GID (gid://shopify/Customer/<n>).",
  ordersCount:
    "Number of orders this customer has placed, or null when the surface did not supply it — checkout supplies it for a verified buyer when the app's protected-customer-data grant covers it; onsite and post-purchase never do. Integer, >= 0, when present. Null-check it, or use sdk.getCustomer() for a count that does not depend on the grant.",
  amountSpent: "Lifetime spend aggregate, when known.",
} as const;

export const AgenticLogicShippingTierDescriptions = {
  _schema: "One rung of a tiered shipping-incentive ladder.",
  threshold:
    "Cart subtotal at which this tier unlocks, in integer minor units of the shipping context's currency.",
  label: "Shopper-facing label for the tier (e.g. 'Free shipping').",
} as const;

export const AgenticLogicShippingContextDescriptions = {
  _schema:
    "Shipping-incentive context for the request. Always present — 'not-applicable' is an in-band state, not an absent object. Thresholds are pre-resolved to the visitor's assigned experiment variation, so rules never re-derive experiment assignment.",
  state:
    "'resolved' = thresholds are known facts for this visitor; 'assumed' = best-effort defaults that may be wrong, so treat them as a hint rather than a fact when steering; 'not-applicable' = the store has no shipping incentive here, and the order may not be shipping at all.",
  currency:
    "ISO-4217 currency code the threshold fields are denominated in. Always present, even when state is 'not-applicable'.",
  freeShippingThreshold:
    "Cart subtotal at which shipping becomes free, in integer minor units — pre-resolved to the visitor's assigned variation's threshold. Absent when there is no free-shipping incentive.",
  remainingToFreeShipping:
    "How much more the visitor must add to qualify for free shipping, in integer minor units. 0 = already qualifies; absent or null = unresolvable.",
  tiers:
    "Tiered incentive ladder in ascending threshold order, when the store runs tiered incentives.",
  delivery:
    "The delivery options Shopify computed for this checkout, as observed facts rather than merchant settings. Present only at checkout, and only once Shopify has computed options (an address or a pickup selection exists); absent onsite, post-purchase, and on first render. Present under every state, including 'not-applicable' for local pickup.",
} as const;

export const AgenticLogicShippingDeliveryOptionDescriptions = {
  _schema:
    "One delivery option Shopify offered the buyer at checkout — a shipping rate or a pickup choice — in the rule envelope's minor-unit money convention. PII-MINIMAL: option facts only, never the destination.",
  title: "Shopper-facing option title, when Shopify reports one.",
  isPickup:
    "True for a pickup option (pickup point or pickup location) rather than a shipping rate. Pickup options carry no cost and no transit estimate.",
  selected:
    "True when this is the buyer's currently selected option in its delivery group.",
  cost: "The option's cost after shipping discounts, in integer minor units of `currency`. 0 = free. Absent on pickup options, which have no cost.",
  currency: "ISO-4217 currency code of `cost`. Absent with it.",
  carrierName: "Carrier presenting the option, when Shopify reports one.",
  transitDays:
    "Estimated time in transit in whole days, when Shopify reports one — `min` rounded down, `max` rounded up. Shipping options only.",
} as const;

export const AgenticLogicShippingDeliveryDescriptions = {
  _schema:
    "Observed delivery facts for the buyer's checkout: the options Shopify computed and which one is selected. Facts, not merchant settings — steer a selection by them directly.",
  isLocalPickup:
    "True when every selected delivery option is a pickup option — the buyer is not shipping anything.",
  selected:
    "The buyer's currently selected option, when exactly one delivery group has a selection. Absent before the buyer picks, and when multiple groups (a subscription plus a one-time purchase) select different options — read `options` then.",
  options:
    "Every option Shopify computed, flattened across delivery groups, in Shopify's order. A zero-cost non-pickup option means the buyer already qualifies for free shipping.",
} as const;

export const AgenticLogicRequestDescriptions = {
  _schema:
    "Everything a recommendation rule may read for one evaluation. Conventions: ids are Shopify GIDs (gid://shopify/<Kind>/<n>); all money is integer minor units with an explicit ISO-4217 currency field; timestamps are ISO-8601 UTC strings.",
  surface:
    "Surface the recommendation will render on. There is deliberately no 'headless' value — API callers declare the surface they render.",
  placementId: "Identifier of the placement being filled.",
  cart: "Current cart, or null when the surface has no cart (post-purchase). An empty cart is legal onsite — branch on surface, not on cart == null.",
  order:
    "Completed order — first-class on the post-purchase surface only; null elsewhere.",
  pageProduct:
    "Product the placement's page is about — onsite PDP placements only; null everywhere else.",
  customer: "Identified customer, or null for guests.",
  shipping:
    "Shipping-incentive context. Required — 'not-applicable' is expressed in-band via its state field, never by omitting the object.",
  cartFingerprint:
    "Opaque client-supplied fingerprint of the cart state this request was built from. The engine echoes it verbatim on the result envelope so the rendering surface can compare and discard late results.",
  country:
    "ISO-3166-1 alpha-2 code of the market this evaluation runs in. Platform-stamped and PINNED for the whole invocation, exactly like evaluatedAt: serving stamps the caller's own country, and every other lane (preview, publish gates, chain cost, required tests) stamps the scenario's country or the platform default. Every catalog read the SDK makes executes under it, so availability and presentment prices a rule sees are this market's — read it instead of inferring a market from a product's currency.",
  evaluatedAt:
    "The instant this evaluation judges the world at, epoch milliseconds UTC. Platform-stamped and PINNED for the whole invocation — this is NOT a live clock, and a rule reading it twice gets the same answer. It is the only sanctioned source of 'now' in rule code: Date.now() is refused both at save time and at runtime, because pinning is what lets the stable-output gate invoke a scenario twice and demand identical envelopes. Callers never supply it; the broker stamps every serving, preview, API and evaluate ingress, and the publish gates stamp once per job.",
} as const;

/**
 * The request as a CALLER authors it — everything above except `evaluatedAt`.
 *
 * Same entries by reference: this is one contract described once, split only by
 * who supplies the clock.
 */
export const AgenticLogicIngressRequestDescriptions = {
  _schema:
    "The request side a caller supplies: an HTTP evaluate/serve body, or a publish-gate validation scenario. Identical to the rule-facing request except that it carries no `evaluatedAt` — the platform stamps that, so a caller can neither set nor forge the instant a rule is judged at.",
  surface: AgenticLogicRequestDescriptions.surface,
  placementId: AgenticLogicRequestDescriptions.placementId,
  cart: AgenticLogicRequestDescriptions.cart,
  order: AgenticLogicRequestDescriptions.order,
  pageProduct: AgenticLogicRequestDescriptions.pageProduct,
  customer: AgenticLogicRequestDescriptions.customer,
  shipping: AgenticLogicRequestDescriptions.shipping,
  country:
    "OPTIONAL. ISO-3166-1 alpha-2 code of the market this request should be evaluated in, e.g. 'CA'. Omit it (or send null) and the platform evaluates in its default market, 'US'. Name it whenever the cart is denominated in a non-shop currency, so the catalog reads run in the same market the money does.",
  cartFingerprint: AgenticLogicRequestDescriptions.cartFingerprint,
} as const;

export const AgenticLogicItemDiscountDescriptions = {
  _schema:
    "Discount attached to a recommended item. RESERVED at envelope version 1 and read by NO surface — typed now so the envelope never breaks when it activates, but emitting it today prices nothing and is REFUSED at ingress. A rule does not price what it recommends: the discount and the struck-through price are the PLACEMENT's, set by the merchant on the block or component the rule is attached to.",
  type: "'percentage' or 'fixed'.",
  value:
    "For 'percentage': percent points (10 = 10% off). For 'fixed': integer minor units in the shop currency.",
} as const;

export const AgenticLogicBundleChildDescriptions = {
  _schema: "One child line of a bundle-kind recommendation item.",
  kind: "Kind of the child item (scopes how `id` is interpreted).",
  id: "Shopify GID of the child, scoped by `kind`.",
  quantity: "Units of the child included in the bundle. Positive integer.",
} as const;

export const AgenticLogicResultItemDescriptions = {
  _schema:
    "One recommended item. `kind` scopes how `id` is interpreted. Consumers MUST ignore kinds they do not understand — unknown kinds are never errors; the kind list exists so gates can validate rule output, not so surfaces can reject envelopes.",
  kind: "Item kind. Consumers ignore kinds they don't understand — never errors.",
  id: "Shopify GID of the recommended entity, scoped by `kind` (e.g. gid://shopify/Product/<n> for kind 'product'). For kind 'bundle' the id is the Product GID of the bundle's parent/anchor product, never a merchant-chosen key — there is no gid://shopify/Bundle/<n>.",
  defaultVariantId:
    "Which variant is pre-selected for the shopper — the core output for product-kind items, so surfaces render a purchasable default without re-deriving one.",
  allowedVariantIds:
    "Exhaustive list of variant GIDs the shopper may add, when the merchant restricted the item to a subset of its variants. ABSENT means unrestricted (every variant is allowed); present means only these are. Surfaces that mint per-variant discount signatures MUST sign only these — treating absence and an empty list alike would mint a signature for a variant the merchant excluded.",
  sellingPlanId:
    "Shopify selling-plan GID to attach when the item is added, for subscription recommendations. RESERVED in V1.",
  intent:
    "'add' adds the item to the cart. 'replace' asks the surface to swap out the originating cart line and is materialized by NO surface today — emitting it is refused at publish rather than silently downgraded, because a surface that can only Add would put the swap target alongside the item it was meant to replace. Recommend the swap target with intent 'add', or wait for a surface that supports it. RESERVED in V1.",
  discount:
    "RESERVED in V1 and read by NO surface — setting it prices nothing, and it is REFUSED at ingress. Pricing is the placement's, never the rule's. Activating this field bumps the envelope version.",
  sourceBranch:
    "Path-capable label of the rule branch that produced this item (e.g. 'colour-match/size-in-stock'), for telemetry and debugging.",
  children:
    "Child lines when kind is 'bundle'. Each child's `id` must match its own `kind` — a 'product' child carries a Product GID, a 'variant' child a ProductVariant GID. Schema-level tolerance: presence on other kinds is a gate concern, not a parse error.",
} as const;

export const AgenticLogicBranchFiredDescriptions = {
  _schema:
    "Which rule branch terminated the evaluation — the branch whose items were returned.",
  base: "The literal label passed to attempt('label') in the rule source — statically lexable, so publish gates can verify it exists in the code.",
  instance:
    "Runtime instance qualifier when one branch runs multiple passes (e.g. the tag this pass ran against). Present ONLY when the attempt supplied one via `attempt(base, fn, { instance })`; the key is omitted entirely otherwise, so consumers must treat its absence as normal and must never assert deep equality against an object that assumes it.",
} as const;

export const AgenticLogicResultEnvelopeDescriptions = {
  _schema:
    "What a recommendation rule returns. Conventions: ids are Shopify GIDs; money is integer minor units with explicit ISO-4217 currency; timestamps are ISO-8601 UTC. Engine errors and timeouts collapse to an empty result behaviorally (the surface follows the placement's onEmpty policy) but stay distinct in evaluation telemetry.",
  version:
    "Envelope schema version. Activating any reserved shape bumps this version — consumers negotiate on it, it never breaks silently. Independent of the REST API path version.",
  items:
    "Recommended items in render order. An empty array is a valid result — a miss, not an error.",
  branchFired: "The terminal rule branch that produced this result.",
  cartFingerprint:
    "Echo of the request's cartFingerprint, verbatim. The rendering surface compares it against the current cart state and discards late results that no longer match.",
  warnings:
    "Non-fatal notices from the evaluation, surfaced to telemetry and debugging tools.",
  metadata:
    "Rule-authored metadata. Free-form and read by NOTHING — no key here is interpreted. A rule decides WHICH products are recommended; it never prices them. The discount and the struck-through price belong to the placement the rule is attached to, where the merchant sets them.",
} as const;

export const AgenticLogicContextDescriptions = {
  _schema: "Evaluation context the runner supplies alongside the request.",
  isPreview:
    "True when the evaluation is a preview (editor/test-lab) run rather than live traffic.",
  slotsNeeded:
    "How many recommendation slots the placement wants filled. Non-negative integer, at most 50 — ZERO is reachable, because a placement can resolve to no slots, so a rule must answer zero with an empty envelope before it reads anything.",
} as const;

export const AgenticLogicInvocationDescriptions = {
  _schema:
    "The full payload handed to a recommendation rule invocation: the request, evaluation context, and org-scoped secrets.",
  request: "The recommendation request being evaluated.",
  candidates:
    "Typed and RESERVED: upstream candidate items for collection/search re-ranking and baseline-to-variant chaining. Never populated in V1; activating it bumps the envelope version.",
  context: "Evaluation context (preview flag, slots needed).",
  secrets:
    "Org-scoped secret values injected by the runner at invocation time — names map to the org's configured secrets; values never appear in stored payloads or telemetry.",
} as const;

export const CheckoutBuyerShippingAddressDescriptions = {
  _schema:
    "The buyer's shipping address as read from the Checkout UI API. CLIENT-ASSERTED and NOT PII-minimal — this is part of the one wire object permitted to carry buyer PII, and it terminates at the backend projection rather than reaching the rule envelope. Every field except `company` requires Shopify protected-customer-data LEVEL 2; an unapproved field simply arrives undefined, so nothing downstream may require one.",
  address1: "Street address line 1. Requires PCD level 2.",
  address2:
    "Street address line 2 (apartment, suite, unit). Requires PCD level 2.",
  city: "City, town, or village. Requires PCD level 2.",
  provinceCode:
    "Province/state code of the destination. Requires PCD level 2. Reaches the shipping resolver as part of `destination`; never the rule envelope.",
  countryCode:
    "ISO 3166-1 alpha-2 country of the SHIPPING ADDRESS. Requires PCD level 2. Distinct from the request's localization country: this is where the order actually ships, so the resolver prefers it for delivery-zone selection.",
  zip: "Postal or ZIP code. Requires PCD level 2.",
  company:
    "Company name on the address. The only address field available at PCD level 1.",
  name: "Full name on the address. Requires PCD level 2.",
} as const;

export const CheckoutBuyerDeliveryOptionDescriptions = {
  _schema:
    "One delivery option Shopify computed for the buyer, in full detail. The PII-minimal AgenticLogicDeliveryGroupRate remains the shape the shipping resolver consumes; this richer read exists for buyer-context derivation and terminates at the backend projection.",
  handle:
    "Stable Shopify handle identifying the option within its delivery group.",
  title: "Shopper-facing option title, when available.",
  type: "Option type as Shopify reports it — 'shipping', 'local', 'pickupPoint', or 'pickup'. Pickup types carry no cost and no delivery estimate.",
  costMajor:
    "The option's cost in MAJOR currency units (e.g. 4.9 = $4.90), exactly as read. Absent on pickup-location options, which have no cost. Deliberately not minor units: the extension has no currency-exponent table.",
  costAfterDiscountsMajor:
    "The option's cost in MAJOR units after shipping discounts. A free-shipping discount shows up here as an observed zero, which the resolver may trust.",
  currency: "ISO-4217 currency code of the cost fields.",
  carrierName: "Carrier presenting this option, when Shopify reports one.",
  deliveryEstimateSeconds:
    "Estimated time in transit, in SECONDS — the unit the Checkout UI API reports on `deliveryEstimate.timeInTransit`. Shipping options only; pickup options carry none.",
} as const;

export const CheckoutBuyerDeliveryGroupDescriptions = {
  _schema:
    "One delivery group from the Checkout UI API, with its full option list. Shopify has no `destination` on a delivery group — the destination is the shipping address, carried separately on the buyer context.",
  groupType: "Shopify's group type — 'oneTimePurchase' or 'subscription'.",
  isDeliveryRequired:
    "False when the group's lines need no delivery (digital goods).",
  selectedDeliveryOptionHandle:
    "Handle of the buyer's currently selected option in this group, when one is selected.",
  deliveryOptions: "Every delivery option Shopify computed for this group.",
} as const;

export const CheckoutBuyerCustomerDescriptions = {
  _schema:
    "The logged-in customer as read from the Checkout UI API's buyer identity. CLIENT-ASSERTED: the backend verifies only that `id` matches the verified session token's `sub`, and drops the whole block (and the address) on a mismatch. `tags` is deliberately absent — it does not exist on the checkout Customer interface; read tags via sdk.getCustomer. `storeCreditAccounts` (@private) and `acceptsMarketing` (@deprecated) are likewise omitted on purpose.",
  id: "Customer GID (gid://shopify/Customer/<id>). The ONLY verified field. PCD level 1.",
  email: "Email on the customer's account. Requires PCD level 2.",
  phone: "Phone on the customer's account. Requires PCD level 2.",
  name: "Customer's full name. Requires PCD level 2.",
  ordersCount:
    "Orders the customer has previously placed with this shop. PCD level 1, and the one buyer-context field that reaches the rule envelope — it replaces the hardcoded 0 checkout previously fabricated for every logged-in buyer.",
  acceptsEmailMarketing:
    "Whether the customer opted in to email marketing. PCD level 1. Consent-to-market is not consent-to-personalize; this does not reach the rule envelope.",
  acceptsSmsMarketing:
    "Whether the customer opted in to SMS marketing. PCD level 1. Does not reach the rule envelope.",
} as const;

export const CheckoutBuyerContextDescriptions = {
  _schema:
    "Full customer/shipping context forwarded by the Checkout UI extension. CLIENT-ASSERTED, NOT VERIFIED except `customer.id`, and the ONE object on the wire permitted to carry buyer PII. It TERMINATES at the backend's projectCheckoutBuyerContext: only {id, ordersCount} reaches the rule envelope and only country/province reaches the shipping resolver. AgenticLogicDeliverySignals keeps its separate PII-minimal guarantee unchanged.",
  customer:
    "The logged-in customer, when the buyer is recognised. Absent for guest checkout.",
  shippingAddress:
    "The buyer's destination address, when entered and PCD level 2 is approved.",
  deliveryGroups:
    "Delivery groups with full option detail, as Shopify computed them.",
} as const;

export const AgenticLogicDeliveryGroupRateDescriptions = {
  _schema:
    "One delivery option Shopify computed for the buyer's checkout, as read from the Checkout UI API. Costs are MAJOR units (e.g. 4.9 = $4.90) exactly as the extension read them — the shipping-context resolver owns the conversion to integer minor units; nothing renders these numbers directly.",
  costMajor:
    "The option's cost in MAJOR currency units as read from the Checkout UI API (deliveryOption.cost.amount). 0 = free. Deliberately not minor units: the extension has no currency-exponent table, so conversion happens server-side in the resolver.",
  currency: "ISO-4217 currency code of costMajor.",
  selected:
    "True when this option is the buyer's currently selected delivery option in its delivery group.",
  isPickup:
    "True when the option is a pickup option (pickup point or pickup location) rather than a shipping rate.",
  title: "Shopper-facing option title, when available.",
  carrierName:
    "Carrier presenting the option, when Shopify reports one. Not an address field: it names the carrier, not the destination.",
  transitSeconds:
    "Estimated time in transit in SECONDS, as the Checkout UI API reports on `deliveryEstimate.timeInTransit`. Shipping options only; pickup options carry none.",
} as const;

export const AgenticLogicDeliverySignalsDescriptions = {
  _schema:
    "Delivery facts forwarded from the live checkout extension so the shipping-context resolver can mark checkout contexts 'resolved' and detect local pickup. PII-MINIMAL GUARANTEE: presence booleans and rate costs ONLY — no address field (country, city, zip, street, name, ...) may EVER be added here; the buyer's destination stays inside Shopify's checkout sandbox.",
  hasShippingAddress:
    "True when the buyer has entered a shipping address (a PRESENCE boolean — never the address itself). Drives the resolved/assumed split for checkout-surface shipping contexts.",
  isLocalPickup:
    "True when every selected delivery option is a pickup option — the visitor is not shipping anything, so shipping incentives are not applicable.",
  deliveryGroupRates:
    "The delivery options Shopify computed for this checkout, flattened across delivery groups. A zero-cost non-pickup option means the buyer already qualifies for free shipping.",
} as const;

export const AgenticLogicShippingResolverCartDescriptions = {
  _schema:
    "Cart totals the resolver derives 'remaining to free shipping' from. Null when the surface has no cart (post-purchase).",
  subtotal:
    "Cart merchandise subtotal in integer MINOR units of `currency`, pre order-level discounts — the same value as AgenticLogicCartData.subtotal, passed through by the broker. The vault's discount-moves-the-goalpost edge is documented on the ticket, not solved in V1.",
  currency: "ISO-4217 currency code `subtotal` is denominated in.",
} as const;

export const AgenticLogicShippingResolverInputDescriptions = {
  _schema:
    "Input to resolveAgenticLogicShippingContext — everything the shipping-context resolver needs to produce the request's AgenticLogicShippingContext. The resolver loads the four threshold sources (variation shippingRateGroups, progress-bar tiers, Shopify zone rates, CSP presence) itself; callers supply only per-request facts.",
  orgId: "Organization whose shipping configuration is being resolved.",
  surface:
    "Surface the recommendation renders on. Onsite is 'assumed' by construction; checkout resolves once an address exists; post-purchase has nothing to reason about.",
  shortVariationIds:
    "The visitor's assigned variation short-ids (from the igTestGroups attribute). The resolver pre-resolves thresholds to the assigned variation so rules never re-derive experiment assignment.",
  cart: "Cart totals, or null when the surface has no cart.",
  delivery:
    "Checkout delivery signals, when the surface has them (checkout only). Absent onsite and post-purchase.",
  countryCode:
    "ISO 3166-1 alpha-2 country the request is localized to, as the surface caller context reports it. It selects the merchant's Shopify delivery zone, so it is present on every surface — including onsite, which carries no delivery block at all. A market signal, not an address field. It is NOT the shipping address's country; that arrives separately as `destination` when checkout has one.",
  destination:
    "The SHIPPING ADDRESS's country/province, when the checkout surface supplied a verified-session address (Shopify PCD level 2). Zone selection prefers it over `countryCode`, which is only the localization country and disagrees on cross-border shipments. Absent onsite, post-purchase, without PCD level 2, and before the buyer types an address. A sibling of countryCode and NOT part of the delivery block, which forbids every address key.",
} as const;

export const AgenticLogicEvaluationTelemetryDescriptions = {
  _schema:
    "Telemetry facts for one rule evaluation. Emission is the broker's job; the shape is typed here so the vocabulary is pinned with the envelope. Errors and timeouts collapse to empty behaviorally (the surface follows the placement's onEmpty policy) but stay distinct here.",
  outcome:
    "'result' = items returned; 'empty' = valid miss (zero items); 'error' = the evaluation failed or timed out and the surface fell back to empty behavior.",
  errorClass:
    "Free-form classifier of the failure when outcome is 'error'. Deliberately not an enum yet — the vocabulary gets pinned once real failure modes exist.",
  datasetRevisions:
    "The dataset revisions the evaluation's pinned reads resolved to, one entry per distinct key read. This is the field the impression stamp records (persistence lands with the impression store, M4).",
  removedByExclusion:
    "Items removed by serving exclusions, with only the item id and the approved removal reason.",
  attempts:
    "Per-attempt chain records in execution order, captured by the SDK runtime's accumulator — never authored by rule code. Fallback rate = attempt 1 contributed 0. Absent means no chain ran, never 'zero attempts'.",
  evaluationSource:
    "Which lane produced this evaluation: 'serving' = a shopper-facing surface, 'api' = the metered external/MCP evaluate endpoint, 'preview' = authoring preview. 'api' and 'preview' evaluations are never impressions — readouts must exclude them.",
  executionTier:
    "Which executor ran the rule: 'template' = the shared warm template runner, 'custom' = the org's own rule Lambda. Absent when the evaluation exited before an assignment resolved, so nothing ran. Not the same vocabulary as the stored version execution kind.",
  deadlineMs:
    "The RELATIVE time budget the evaluation ran under, in milliseconds — min(surface hard cap, the assignment's budgetMs). The absolute epoch deadline is meaningless in a readout and already rides the invocation as system.deadlineAtMs. Absent when the evaluation exited before an assignment resolved.",
  fallback:
    "What the placement's onEmpty policy did with an empty rule result: 'not-applicable' = the rule returned items, so no policy ran; 'hidden' = onEmpty is 'hide'; 'materialized' = the smart-recs fallback supplied the items; 'unavailable' = smart-recs was asked and had nothing to give; 'failed' = the fallback read itself errored. This is what separates empties a readout would otherwise collapse into one bucket.",
  classifierBreakerTripped:
    "Whether the org-scoped storefront-classifier breaker was already open when this evaluation started, so the rule was short-circuited rather than executed. Absent when the evaluation exited before the breaker was consulted.",
} as const;

export const AgenticLogicTelemetryEventPayloadDescriptions = {
  _schema:
    "One SERVED RULE EVALUATION, emitted server-side by the serving broker. This counts evaluations the engine completed, NOT blocks a shopper saw: the surfaces decide separately whether to render, and the visibility facts that used to gate the client-side impression (onsite dwell, checkout post-filter render, pageViewId, checkoutToken) exist only in the browser and are deliberately not reproduced here. A readout that needs true impression grain needs a surface signal this event cannot supply.",
  eventType:
    "Pinned event-type discriminator. The analytics lane routes and lands on this exact string, so it is a constant and never derived.",
  engine:
    "Always 'agentic-logic'. Present so a lane carrying more than one recommendation engine can filter without joining.",
  orgId: "Organization the evaluation ran for.",
  shopId:
    "The org's myshopify domain, the same grain the rest of the tracking lane keys on. Read alongside the evaluation rather than blocking it, so a read that has not landed yet leaves the key absent rather than delaying the response.",
  surface:
    "Surface the evaluation was requested for, as the request declared it. Taken from the request and not from the resolved assignment, so the exits that fire before an assignment resolves still carry it.",
  placementId:
    "Placement the evaluation was requested for. Same source and same reason as `surface`.",
  visitorId:
    "The shopper identity the evaluation was seeded with. The only visitor identity this domain has - there is no session or anonymous id.",
  variationId:
    "Variation the shopper was assigned, when the caller had one. Absent means the caller reported no assignment, which is its own readout bucket and not an error.",
  invocationId:
    "Id of the trusted invocation record this evaluation minted. Absent on the exits that fire before an assignment resolved, because nothing was minted. Doubles as the dedupe key and as the join key for any later per-render signal.",
  sessionId:
    "Browser session identifier supplied by a shopper-facing caller. Absent for API evaluations and callers that cannot observe one.",
  pageViewId:
    "Browser page-view identifier supplied by a shopper-facing caller. Absent for API evaluations and callers that cannot observe one.",
  checkoutTokenHash:
    "SHA-256 checkout-token correlation value from trusted checkout or order context. The raw token is never sent. Absent outside checkout and post-purchase contexts.",
  browserVisibility:
    "Document visibility state when the caller started the evaluation. This is request-time browser state, not proof that the component entered the viewport.",
  evaluationSource:
    "Lane that produced the evaluation: 'serving' for shopper surfaces or 'api' for the metered evaluate endpoint. Preview is excluded. Impression readouts must select serving and join a render event.",
  evaluatedAtMs:
    "When the evaluation started, epoch milliseconds, from the broker's injected clock.",
  durationMs:
    "Wall-clock milliseconds the evaluation took, measured across the same span the serving duration metric reports.",
  ruleId:
    "Rule that produced the result. Absent when the evaluation exited before an assignment resolved, so no rule was chosen.",
  ruleVersion:
    "Published version of `ruleId` that ran. Travels with `ruleId` - a version alone cannot say which rule it belongs to.",
  templateId:
    "Template the rule was generated from, when it was. Absent for a rule with no template behind it.",
  templateVersion:
    "Version of `templateId`. Travels with `templateId` for the same reason `ruleVersion` travels with `ruleId`.",
  branchFired:
    "Stable base label of the branch that produced the result. The join key readouts group on; the runtime instance qualifier is never fused into it.",
  branchFiredInstance:
    "Runtime instance qualifier of the fired branch, when the branch had one. Its own key so `branchFired` stays a stable label. Present ONLY when the attempt supplied one via `attempt(base, fn, { instance })`; the key is omitted entirely otherwise, so consumers must treat its absence as normal and must never assert deep equality against an object that assumes it.",
  fallbackDepth:
    "Index of the first chain attempt that contributed items: 0 = the primary branch served, >0 = a fallback branch served. Absent when no chain ran or no attempt contributed. This, not `outcome`, is the per-evaluation fallback-rate signal.",
  outcome:
    "'result' = items were returned; 'empty' = a valid miss; 'error' = the evaluation failed or timed out and the surface fell back to empty behavior.",
  errorClass:
    "Classifier of the failure when `outcome` is 'error'. Free-form on purpose - the vocabulary gets pinned once real failure modes exist.",
  executionTier:
    "Which executor ran the rule: 'template' = the shared warm template runner, 'custom' = the org's own rule Lambda. Absent when the evaluation exited before an assignment resolved.",
  deadlineMs:
    "The RELATIVE time budget the evaluation ran under, in milliseconds - min(surface hard cap, the assignment's budget). Absent when the evaluation exited before an assignment resolved.",
  fallback:
    "What the placement's onEmpty policy did with an empty rule result. This is what separates empties a readout would otherwise collapse into one bucket.",
  classifierBreakerTripped:
    "Whether the org-scoped storefront-classifier breaker was already open when this evaluation started, so the rule was short-circuited rather than executed. Absent when the evaluation exited before the breaker was consulted.",
  itemCount:
    "How many items the shipped envelope carried. Zero is a real value and is exactly what `outcome` 'empty' means.",
  items:
    "WHICH items the shipped envelope carried, in the order the rule returned them - the identity half of each item only (`AgenticLogicResultItemStamp`), never the presentation the rule chose. Bounded by `settings.maxItems` plus the onsite rotation reserve, so it is safe to carry inline. Empty exactly when `itemCount` is 0; `itemCount` stays the count a readout aggregates on, this is what a run detail shows. The broker always sets it, so an ABSENT list means the row was landed before this key existed - not that the run returned nothing.",
  readCache:
    "What the shared Upstash read cache did for this evaluation. `prefetch` = the broker's manifest prefetch (status plus fresh/stale/miss counts; absent when the version had no read manifest). `reads` = the rule's own SDK reads through the cache, one row per read kind with fresh/stale/miss counts (empty when it made none; absent when the counts could not be collected). Absent on exits before an invocation was minted, and on rows landed before this key existed.",
} as const;

export const AgenticLogicResultItemStampDescriptions = {
  _schema:
    "The identity half of a result item — what a surface rendered, without the presentation the rule chose.",
  kind: "Item kind, as it arrived on the envelope.",
  id: "Shopify GID of the rendered item, as it arrived on the envelope.",
  defaultVariantId:
    "Variant the surface would add to cart for this item, when the envelope carried one.",
  sourceBranch:
    "Path of attempt labels that produced this item (nested chains join with '/'), when the envelope carried one. Per-slot click/ATC attribution joins to the producing branch through this, not the terminal branchFired label.",
} as const;

export const AgenticLogicSurfaceRenderMetadataDescriptions = {
  _schema:
    "What one surface actually rendered from one evaluation. Purely a projection of the result envelope and the evaluation telemetry — it introduces no fact neither of them already carries, so a reader can always trace a value back to its source.",
  invocationId:
    "Trusted evaluation invocation id. A browser render event uses it to join the confirmed impression to the server evaluation. Absent when evaluation stopped before an invocation was minted.",
  placementId:
    "Placement the surface rendered into. Same value the request carried.",
  ruleId:
    "Rule that produced the result. A version alone cannot say WHICH rule ran, and this stamp is the forensic trail when a merchant reports a bad recommendation.",
  templateId:
    "Template the rule is configured from, or null for a code-managed rule. ruleVersion + templateId + templateVersion together determine the behavior that ran.",
  ruleVersion:
    "Rule version that produced the result. 0 means the evaluation ran against a draft, which only authoring does — serving always stamps a real version.",
  templateVersion:
    "Template version executed, or null when a code-managed rule produced the result.",
  items:
    "Rendered items, in render order — the same order the envelope's items arrived in.",
  branchFired: "The terminal rule branch that produced this result.",
  attempts:
    "Per-attempt chain records in execution order — a projection of the evaluation telemetry. Absent means no chain ran.",
  outcome:
    "'result' = items returned; 'empty' = valid miss (zero items); 'error' = the evaluation failed or timed out and the surface fell back to empty behavior.",
  errorClass:
    "Free-form classifier of the failure when outcome is 'error'; absent otherwise.",
  cartFingerprint:
    "The fingerprint this render was judged against. A surface discards a result whose fingerprint no longer matches the cart it holds.",
} as const;

export const EvaluateAgenticLogicRequestDescriptions = {
  _schema:
    "Run one evaluation against a supplied request context and return the versioned result envelope. Metered per organization; never recorded as an impression.",
  request:
    "The full serving request envelope. `placementId` must be an API-created placement with an enabled assignment; `surface` declares the surface your rendering context corresponds to.",
  slotsNeeded:
    "How many recommendation slots the caller intends to render. Non-negative integer, at most 50. Zero is legal and asks the rule for an empty result; it is not an error.",
  visitorId:
    "Seed for the deterministic shuffle (with the rule version). Omitted = a fresh random id per call. Pass a fixed value to make repeat calls comparable against pinned fixtures; live results still drift with the catalog.",
  country:
    "ISO 3166-1 alpha-2 country the evaluation should price and read Storefront data for.",
  variationId:
    "Experiment cell to resolve the assignment in. Null or omitted = the placement's global cell. A named variation resolves its own enabled cell when it has one, and otherwise falls back to the global cell — a disabled variation cell does not suppress the global default.",
} as const;

export const EvaluateAgenticLogicResponseDescriptions = {
  _schema:
    "The evaluation result: the versioned envelope plus its telemetry facts.",
  result:
    "The versioned result envelope and evaluation telemetry. `telemetry.evaluationSource` is 'api' on this endpoint.",
} as const;

export const ServeAgenticLogicRequestDescriptions = {
  _schema:
    "Serve one shopper-facing slot set for a pilot organization. Unmetered, and the only lane where a real buyer exists: the buyer IP comes from the request's network peer, never from this body.",
  request:
    "The full serving request envelope. `placementId` must be a placement with an enabled assignment; `surface` declares the surface being rendered.",
  slotsNeeded:
    "How many recommendation slots the caller intends to render. OPTIONAL: omit it and the rule's own `settings.maxItems` supplies the count, which is the normal case now that the rule owns how many products it returns. Pass a value only to render a slot shape other than the rule's own. Non-negative integer, at most 50. Zero is legal and asks the rule for an empty result; it is not an error.",
  visitorId:
    "The plugin's visitor id. Required: it seeds the deterministic shuffle, so a shopper who reloads the same page keeps the same order.",
  country:
    "ISO 3166-1 alpha-2 country the evaluation should price and read Storefront data for.",
  variationId:
    "Experiment cell to resolve the assignment in. Null or omitted = the placement's global cell. A named variation resolves its own enabled cell when it has one, and otherwise falls back to the global cell — a disabled variation cell does not suppress the global default.",
  browserContext:
    "Optional browser correlation facts available at request time: session id, page-view id, and document visibility. The server does not invent missing values.",
} as const;

export const ServeAgenticLogicResponseDescriptions = {
  _schema:
    "The serving result: the versioned envelope plus its telemetry facts.",
  result:
    "The versioned result envelope and evaluation telemetry. `telemetry.evaluationSource` is 'serving' on this endpoint.",
} as const;

// ---------------------------------------------------------------------------
// Product-query SDK contract (src/functions/agentic-logic/sdk/) — the
// rule-visible types behind `sdk.*` and the chain primitives. Same
// conventions as above: GIDs, integer minor units with ISO-4217 currency
// alongside, ISO-8601 UTC timestamps.
// ---------------------------------------------------------------------------

export const AgenticLogicTagFilterDescriptions = {
  _schema:
    "Server-side tag predicate. Compiles to Storefront `products(query:)` tag terms.",
  anyOf: "Match products carrying AT LEAST ONE of these tags (OR).",
  allOf:
    "Match products carrying EVERY one of these tags (AND). NOT usable together with `collectionId`: repeated Storefront collection tag filters combine with OR, so the AND cannot be expressed server-side. Inside a collection, use `where.postFetch.tags.allOf`, which runs on the paged-scan path.",
} as const;

export const AgenticLogicStringAnyOfFilterDescriptions = {
  _schema:
    "Server-side any-of predicate over a single string field (product type or vendor).",
  anyOf: "Match products whose field equals AT LEAST ONE of these values (OR).",
} as const;

export const AgenticLogicPriceFilterDescriptions = {
  _schema:
    "Server-side price-range predicate in integer minor units of the SHOP currency. The compiler converts to the decimal amounts the Storefront query syntax expects; thresholds stay shop-currency in V1.",
  gte: "Minimum price, inclusive. Integer minor units of the shop currency.",
  gt: "Minimum price, exclusive. Integer minor units of the shop currency.",
  lte: "Maximum price, inclusive. Integer minor units of the shop currency.",
  lt: "Maximum price, exclusive. Integer minor units of the shop currency.",
} as const;

export const AgenticLogicTitleFilterDescriptions = {
  _schema:
    "Server-side title predicate supported by Shopify search syntax. NOT usable together with `collectionId` — the collection products field has no title filter. Inside a collection, use `where.postFetch.title.contains`.",
  prefix:
    "Match titles starting with this text (compiles to `title:text*`). PREFIX ONLY — for a substring anywhere in the title, use `where.postFetch.title.contains`.",
} as const;

export const AgenticLogicStorefrontVariantOptionFilterDescriptions = {
  _schema: "One Storefront product-variant option filter.",
  name: "Variant option name configured on the product (for example, Size).",
  value: "Variant option value to match (for example, Medium).",
} as const;

export const AgenticLogicStorefrontCategoryFilterDescriptions = {
  _schema: "One Storefront product-category filter.",
  id: "Shopify product taxonomy category id.",
} as const;

export const AgenticLogicStorefrontMetafieldFilterDescriptions = {
  _schema: "One Storefront product, variant, or taxonomy metafield filter.",
  namespace: "Metafield definition namespace.",
  key: "Metafield definition key.",
  value: "Metafield value to match.",
} as const;

export const AgenticLogicStorefrontPriceRangeFilterDescriptions = {
  _schema:
    "Product price bounds for a Storefront collection filter, expressed in integer minor units of the shop currency.",
  min: "Inclusive minimum product price in integer minor units. Defaults to zero in Shopify.",
  max: "Inclusive maximum product price in integer minor units.",
} as const;

export const AgenticLogicStorefrontProductFilterDescriptions = {
  _schema:
    "One Shopify Storefront ProductFilter input for a collection product query.",
  available: "Match products by availability for sale.",
  variantOption: "Match a variant option name and value.",
  category: "Match a Shopify product taxonomy category.",
  taxonomyMetafield: "Match a standard product-attribute metafield.",
  productType: "Match a merchant-defined product type.",
  productVendor: "Match a product vendor.",
  price: "Match an inclusive decimal product price range.",
  productMetafield: "Match a product metafield value.",
  variantMetafield: "Match a variant metafield value.",
  tag: "Match a product tag.",
} as const;

export const AgenticLogicProductServerWhereDescriptions = {
  _schema:
    "Predicates the Storefront API evaluates SERVER-SIDE. Only the keys listed here are server-filterable — everything else belongs under postFetch and runs as a bounded paged scan. The key list is pinned by a drift-guard spec.",
  tags: "Tag predicate (anyOf/allOf). `allOf` is NOT valid together with `collectionId` — use `where.postFetch.tags.allOf` inside a collection.",
  productType: "Product-type predicate (anyOf).",
  vendor: "Vendor predicate (anyOf).",
  price: "Price-range predicate, integer minor units of the shop currency.",
  title:
    "Title prefix predicate. NOT valid together with `collectionId`, and prefix-only — use `where.postFetch.title.contains` for either case.",
  productFilters:
    "Search & Discovery-curated Storefront `ProductFilter` inputs, passed through verbatim to `collection.products(filters:)`. Server-side, but ONLY valid together with `collectionId` — the products root field does not accept filters.",
} as const;

export const AgenticLogicMetafieldRefDescriptions = {
  _schema:
    "Reference to one product metafield by explicit namespace and key. Metafields are read live from Shopify (never synced) and must be storefront-visible per their definition — a hidden metafield surfaces as a missing value, which the authoring flow flags.",
  namespace: "Metafield namespace (e.g. 'custom').",
  key: "Metafield key within the namespace.",
} as const;

export const AgenticLogicMetafieldPredicateDescriptions = {
  _schema:
    "POST-FETCH metafield predicate. The Storefront API cannot filter `products(query:)` by arbitrary metafields, so this predicate runs on the bounded paged-scan path — the type system never pretends otherwise.",
  namespace: "Metafield namespace (e.g. 'custom').",
  key: "Metafield key within the namespace.",
  equals: "Match when the metafield value equals this string exactly.",
  in: "Match when the metafield value equals ANY of these strings.",
  exists: "Match on presence (true) or absence (false) of any value.",
} as const;

export const AgenticLogicOptionValuePredicateDescriptions = {
  _schema:
    "POST-FETCH option-value predicate (e.g. Color anyOf ['Blue']). Runs on the bounded paged-scan path.",
  name: "Option name as configured on the product (e.g. 'Color'). Matched case-insensitively; otherwise exact — 'Colour' does not match a 'Color' option.",
  anyOf:
    "Match products offering AT LEAST ONE of these option values. Values are matched case-insensitively.",
} as const;

export const AgenticLogicProductPostFetchWhereDescriptions = {
  _schema:
    "Predicates that CANNOT run server-side and therefore execute as a bounded, org-cached paged scan — a distinct, identifiable code path whose cost the scan-cost publish gate prices at authoring time.",
  title:
    "Case-insensitive title substring predicate. Shopify only supports trailing prefix wildcards, so true containment runs on the bounded scan path.",
  tags: "Tag predicate evaluated on the scan path. This is the ONLY way to require several tags at once inside a collection: `where.server.tags.allOf` is refused alongside `collectionId`, because repeated Storefront collection tag filters combine with OR. Tag matching is case-sensitive — use the exact Shopify tag. That is this CODE lane; the template lane's in-memory tag filters (`collection`, `shopify-recommendations`, and `tag-query`'s none-of) ignore case instead.",
  metafields: "Metafield predicates (all must match).",
  optionValues:
    "Option-value predicates (all must match). Option name AND values match case-insensitively — 'Color' matches 'color' and 'Blue' matches 'blue' — unlike `tags`, which are exact Shopify identifiers. Case is the only tolerance: names are otherwise exact, so 'Color' and 'Colour' are DIFFERENT options. Entries here are ANDed, so a second entry cannot cover the other spelling; give each spelling its own attempt in a `firstNonEmpty` ladder.",
} as const;

export const AgenticLogicProductWhereDescriptions = {
  _schema:
    "The two-sided filter boundary: `server` predicates compile into the Storefront query; `postFetch` predicates run on the explicit paged-scan path. A rule states on which side each filter runs — there is no auto-classification.",
  server: "Server-side predicates (compiled into the Storefront operation).",
  postFetch: "Post-fetch predicates (bounded paged scan).",
} as const;

export const AgenticLogicProductOrderByDescriptions = {
  _schema:
    "Sort order. Storefront product reads support 'bestSelling', 'price', 'createdAt', 'title', and 'relevance'. 'inventoryTotal' is available only to Admin-backed specialized accessors such as getInStock.",
  field:
    "'bestSelling' | 'price' | 'createdAt' | 'title' | 'relevance' | 'inventoryTotal' — the sort key.",
  direction:
    "'asc' (default) or 'desc'. Compiles to the operation's `reverse` flag.",
} as const;

export const AgenticLogicProductScanDescriptions = {
  _schema:
    "Bounds for the paged-scan path. Only meaningful when postFetch predicates are present; the scan stops at maxProducts and reports truncation in the page's scan telemetry.",
  pageSize: "Products fetched per scan page (max 250; default 250).",
  maxProducts:
    "Hard cap on products scanned before the scan stops and marks the result truncated (max and default 1,000).",
} as const;

export const AgenticLogicProductIncludeDescriptions = {
  _schema: "Extra per-product data to fetch alongside the core product shape.",
  collectionIds:
    "Fetch Storefront-visible collection GIDs for each product. Use true for the first 20, or { first: N } to request 1–250. Use this for post-fetch collection scope checks.",
  metafields:
    "Metafields to fetch (explicit namespace/key each). Values come back on AgenticLogicProduct.metafields; a hidden or absent metafield yields a null value.",
} as const;

export const AgenticLogicProductQueryDescriptions = {
  _schema:
    "The typed product-query builder input for sdk.getProducts. Every compiled operation carries @inContext(country:) — multi-market-correct reads are structural, not opt-in.",
  ids: "Fetch exactly these products (gid://shopify/Product/<n>, or the bare numeric id the Shopify admin shows), batched into one nodes() read. EXCLUSIVE: a query with ids may not also set collectionId, where, orderBy or scan — an ids read resolves the products named and cannot scope, filter, sort or page them, so combining them is a compile-time error, never a silent drop. To restrict a fixed list to a collection, drop ids and express the whole selection with collectionId plus where.",
  collectionId:
    "Scope the query to one collection (gid://shopify/Collection/<n>, or the bare numeric id the Shopify admin shows). Required for where.server.productFilters. Cannot be combined with ids.",
  where: "The server/postFetch filter boundary.",
  orderBy: "Server-side sort.",
  limit:
    "Maximum products to return. Positive integer, max 250 per read; postFetch queries may scan more than they return (see scan).",
  scan: "Paged-scan bounds (postFetch queries only).",
  include: "Extra per-product data to fetch (explicit metafield refs).",
} as const;

export const AgenticLogicProductOptionDescriptions = {
  _schema: "One configurable product option and its value domain.",
  name: "Option name (e.g. 'Size').",
  values: "All values the product offers for this option.",
} as const;

export const AgenticLogicProductVariantDescriptions = {
  _schema: "One variant of a product returned by sdk.getProducts.",
  id: "Shopify variant GID (gid://shopify/ProductVariant/<n>).",
  title: "Variant title (e.g. 'Blue / Medium').",
  price:
    "Variant price in integer minor units of the product's `currency`, @inContext of the requesting country.",
  compareAtPrice:
    "Compare-at price in integer minor units, when set. Null/absent when the variant has none.",
  availableForSale:
    "Whether the variant is available for sale as of this (possibly cached) read. Final winners get a live re-check via sdk.checkAvailability.",
  selectedOptions: "The option values identifying this variant.",
} as const;

export const AgenticLogicProductMetafieldDescriptions = {
  _schema: "One metafield value fetched for a product.",
  namespace: "Metafield namespace (e.g. 'custom').",
  key: "Metafield key within the namespace.",
  value:
    "The metafield's raw string value, or null when unset or not storefront-visible.",
} as const;

export const AgenticLogicProductDescriptions = {
  _schema:
    "One product as the SDK returns it: identity, merchandising fields, price range in integer minor units, and variants.",
  id: "Shopify product GID (gid://shopify/Product/<n>).",
  title: "Product title.",
  handle: "Product URL handle.",
  vendor: "Vendor name ('' when unset).",
  productType: "Product type ('' when unset).",
  tags: "All tags on the product.",
  featuredImageUrl: "Featured image URL, or null when the product has none.",
  currency:
    "ISO-4217 currency of every money field on this product (@inContext presentment currency of the requesting country).",
  minPrice: "Lowest variant price, integer minor units of `currency`.",
  maxPrice: "Highest variant price, integer minor units of `currency`.",
  variants: "The product's variants (first 100).",
  options: "The product's configurable options.",
  metafields:
    "Requested metafield values (only present when the query asked for them).",
  collectionIds:
    "Storefront-visible collection GIDs this product belongs to, present only when the read asked for them. Absent means collections were NOT read \u2014 not that the product is in none.",
} as const;

export const AgenticLogicProductPageInfoDescriptions = {
  _schema: "Cursor pagination facts for a product read.",
  hasNextPage: "Whether more products exist beyond this page.",
  endCursor: "Cursor to resume from, or null when the page is empty/last.",
} as const;

export const AgenticLogicScanTelemetryDescriptions = {
  _schema:
    "What the paged-scan path actually did — present only when postFetch predicates forced a scan. The scan-cost gate and evaluation telemetry both consume it.",
  pagesScanned: "Scan pages fetched.",
  productsScanned: "Products examined across all scan pages.",
  truncated:
    "True when the scan stopped before exhausting the catalogue — because it hit its maxProducts bound or because it filled `limit`. Results are a prefix, not the full match set: a rule that ranks them ranks what it read, not the catalogue.",
} as const;

export const AgenticLogicProductPageDescriptions = {
  _schema: "The page of products a query resolved to.",
  items: "The products, in server or post-filtered order.",
  pageInfo: "Cursor pagination facts.",
  scan: "Paged-scan telemetry (postFetch queries only).",
} as const;

export const AgenticLogicCatalogScopeDescriptions = {
  _schema:
    "Restricts a catalog read to collections and/or tags. Both given = products in ANY listed collection AND carrying ANY listed tag.",
  collectionIds:
    "Collection GIDs (gid://shopify/Collection/…). A product matches if it is in any of them.",
  tags: "Product tags; a product matches if it carries any of them. Tag matching is exact after case-folding — Shopify tags are case-insensitive.",
} as const;

export const AgenticLogicBestSellersQueryDescriptions = {
  _schema:
    "Top-selling products over a trailing window, ranked by Shopify analytics (ShopifyQL sales schema) and hydrated through Storefront in the request country. Analytics lag Shopify by minutes to hours; the platform caches the read for 10 minutes. The read_reports grant this needs is required at install, so every store has analytics access — never ask a merchant to confirm it. When a ranking cannot be produced the read gives a 'shopifyql_unavailable' denial, never an empty list.",
  timeWindow: "'24h' | '7d' | '30d' | '90d' trailing window (default '30d').",
  metric:
    "'units' (default, net items sold) or 'revenue' (net sales) — what the ranking orders by.",
  scope:
    "Collections and/or tags to restrict the ranking to. Tag scope narrows the ranking itself; collection scope filters the ranked products, so a tight collection can return fewer than `limit`.",
  limit: "Products to return, in rank order (max 250).",
  excludeOutOfStock:
    "Drop products with no variant available for sale. Applied after ranking, so the list can shrink below `limit`.",
} as const;

export const AgenticLogicInStockQueryDescriptions = {
  _schema:
    "Products whose total tracked inventory (summed across locations) meets a threshold. Admin selects ordered product IDs because Storefront cannot server-filter or sort by this threshold; Storefront supplies the buyer-context product records. Products with inventory tracking off have no total and are NOT returned.",
  minQuantity: "Minimum total inventory quantity, inclusive (default 1).",
  scope: "Collections and/or tags to restrict the read to.",
  orderBy:
    "Sort. Default is highest inventory first ('inventoryTotal' desc); 'price' and 'bestSelling' only sort server-side inside exactly one collection.",
  limit: "Products to return (max 250).",
} as const;

export const AgenticLogicNewArrivalsQueryDescriptions = {
  _schema:
    "Active, published products created (or published) within a trailing window, newest first.",
  timeWindow: "'24h' | '7d' | '30d' | '90d' trailing window (default '30d').",
  dateField:
    "'created' (default) uses the product's creation date; 'published' uses the date it went live on the Online Store.",
  scope: "Collections and/or tags to restrict the read to.",
  limit: "Products to return, newest first (max 250).",
  excludeOutOfStock:
    "Drop products with no variant available for sale. Applied after the read, so the list can shrink below `limit`.",
} as const;

export const AgenticLogicRelatedByAttributeQueryDescriptions = {
  _schema:
    "Other products sharing one attribute with a seed product: any of its tags, its product type, its vendor, or any of its collections. The seed product is never in the result. Admin selects candidate IDs; Storefront supplies the buyer-context product records.",
  productId: "Seed product GID.",
  by: "'tag' | 'productType' | 'vendor' | 'collection' — which attribute of the seed to match on.",
  orderBy:
    "Sort. 'price' and 'bestSelling' sort server-side only when `by` is 'collection' and the seed is in exactly one collection; otherwise post-fetch.",
  limit: "Products to return (max 250).",
  excludeOutOfStock:
    "Drop products with no variant available for sale. Applied after the read, so the list can shrink below `limit`.",
} as const;

export const AgenticLogicRecommendationOptionsDescriptions = {
  _schema:
    "Post-fetch narrowing for getProductRecommendations. Shopify's recommendation read takes no filters; the SDK applies these over the rows Shopify returned, so every option can only shrink the list.",
  limit:
    "Keep at most this many recommendations, in Shopify's order (max 250).",
  scope:
    "Keep only recommendations in any listed collection and/or carrying any listed tag.",
  excludeOutOfStock: "Drop recommendations with no variant available for sale.",
} as const;

export const AgenticLogicSdkCustomerDescriptions = {
  _schema:
    "The richer customer profile a rule reaches LAZILY via sdk.getCustomer() (Admin API, budget-guarded, pays nothing if unused). PII-minimal by contract: ids, counts, money aggregates, tags, timestamps ONLY — no email, name, phone, or address, ever (locked by the PII drift-guard spec).",
  id: "Shopify customer GID (gid://shopify/Customer/<n>).",
  ordersCount: "Lifetime order count. Non-negative integer.",
  amountSpent:
    "Lifetime amount spent, integer minor units + ISO-4217 currency. Absent when unknown.",
  tags: "Merchant-assigned customer tags.",
  createdAt: "When the customer record was created (ISO-8601 UTC).",
  lastOrderAt:
    "When the customer last ordered (ISO-8601 UTC). Null/absent when they never have.",
} as const;

export const AgenticLogicProductCostVariantDescriptions = {
  _schema:
    "Per-variant unit cost row. Variants without a cost set are OMITTED — never zero-filled.",
  variantId: "Shopify variant GID.",
  unitCost:
    "The variant's unit cost (Shopify Admin InventoryItem.unitCost), integer minor units of the shop currency.",
} as const;

export const AgenticLogicProductCostDescriptions = {
  _schema:
    "Unit-cost data for one product, from Shopify Admin InventoryItem.unitCost ONLY (no Snowflake/merchant-COGS fallback). getProductCosts returns an ARRAY of these; products with no cost data at all are omitted entirely — absent row, never a zero-filled one, and never an error.",
  productId: "Shopify product GID.",
  currency: "ISO-4217 shop currency all costs on this row are in.",
  unitCost:
    "The DEFAULT VARIANT's unit cost in integer minor units, or null when the default variant has no cost set. Product-grain reads use this field.",
  variants: "Per-variant cost rows. Variants without a cost set are omitted.",
} as const;

export const AgenticLogicOrgExclusionsDescriptions = {
  _schema:
    "Org-wide exclusions, read-only to rule code via sdk.getOrgExclusions() (pinned per invocation; edits are effective on the NEXT invocation). The platform itself applies these to every rule's output on the serving path, fail-closed — reading them is for awareness and filtering finesse, never enforcement. One JSON row per org; the entity and CRUD live with the rule-lifecycle module.",
  productIds: "Product GIDs that must never be recommended.",
  tags: "Products carrying ANY of these tags must never be recommended.",
  collections:
    "Collection GIDs whose member products must never be recommended.",
  vendors: "Products from ANY of these vendors must never be recommended.",
} as const;

export const AgenticLogicAvailabilityDescriptions = {
  _schema:
    "Live availability row from sdk.checkAvailability — the top-N freshness check that BYPASSES the read cache. Candidates may be cached; final winners get this live re-check.",
  productId: "Shopify product GID.",
  variantId:
    "Shopify variant GID when the check targeted one variant; absent for product-level checks.",
  availableForSale: "Whether the product/variant is available for sale NOW.",
  quantityAvailable:
    "Sellable quantity, or null when it is unknown: a variant with inventory tracking off, or a read whose inventory OAuth scope is missing. A product-level row reports totalInventory, which Shopify gives as 0 (not null) for untracked products — check the variant when tracking matters. A sold-out CONTINUE-policy backorder reads 0 with availableForSale true.",
} as const;

export const AgenticLogicAttemptRecordDescriptions = {
  _schema:
    "Telemetry record for one chain attempt. Chains emit one per attempt so readouts can show exactly which branches ran, contributed, or fell through.",
  base: "The attempt's stable base label (e.g. 'colour-match').",
  instance:
    "Per-invocation instance qualifier (e.g. the seed product handle) for readout grouping. Present ONLY when the attempt supplied one via `attempt(base, fn, { instance })`; the key is omitted entirely otherwise, so consumers must treat its absence as normal and must never assert deep equality against a record that assumes it.",
  contributed:
    "How many of this attempt's items made the final result. Non-negative integer.",
  outcome:
    "'contributed' | 'empty' | 'not-selected' | 'skipped' | 'error' — see AgenticLogicAttemptOutcomeList for exact semantics.",
} as const;

export const AgenticLogicChainResultDescriptions = {
  _schema:
    "What a chain combinator (firstNonEmpty / fillTo) resolves to: the winning items (sourceBranch-stamped, deduped) plus the per-attempt records.",
  items:
    "The selected result items. Each carries sourceBranch = the path of attempt labels that produced it (nested chains join with '/').",
  attempts: "Per-attempt records, in execution order.",
} as const;

export const AgenticLogicSdkDenialDescriptions = {
  _schema:
    "Why the Admin budget guard refused a read. Surfaces as telemetry errorClass 'denied' — NEVER as silently-empty data.",
  reason:
    "'admin_budget_exhausted' (per-window read budget spent), 'admin_concurrency_exhausted' (too many in-flight Admin reads for the shop) 'shopifyql_unavailable' (Shopify analytics could not rank windowed best sellers; read_reports is required at install, so this is a platform-side refusal, not a merchant configuration a rule should ask about), 'integration_client_unavailable' (the org has no connected integration for the client the rule called, e.g. UPS is not connected; chain-fatal, the merchant connects it on the Integrations page) or 'source_not_ready' (a script Source has no built value yet; its first build is scheduled and never runs inline on a read).",
  resource:
    "Which guarded read was denied (e.g. 'customer', 'productCosts', 'catalog:bestSellers').",
  message: "Human-readable denial message.",
} as const;

export const AgenticLogicRuleLogEntryDescriptions = {
  _schema:
    "One structured log entry emitted by rule code through the injected marker logger. Wire format shared with deployed bundles — append-only.",
  level: "Log level ('log' | 'info' | 'warn' | 'error' | 'debug').",
  message: "The log message.",
  details: "Optional structured details payload.",
  invocationId:
    "Broker invocation identifier that correlates this entry with one recorded rule run.",
  timestamp: "When the entry was emitted (ISO-8601 UTC).",
} as const;

export const AgenticLogicSdkReadResultDescriptions = {
  _schema:
    "One resolved SDK read: the hash of the canonicalized request and the response it produced. The hash is what the SDK memoizes a read by within an invocation, so two reads of the same thing cost one round trip.",
  requestHash:
    "sha256 hex of the canonical (sorted-key JSON) request, prefixed by the port method.",
  response: "The response, verbatim.",
} as const;

// ---------------------------------------------------------------------------
// Datasets (src/functions/agentic-logic/datasets/) — the generic,
// org-scoped dataset registry behind sdk.getDataset(key): agent authored,
// schema'd as data, pinned per invocation, live on edit, one row per dataset
// updated in place.
// ---------------------------------------------------------------------------

export const AgenticLogicDatasetSummaryDtoDescriptions = {
  _schema:
    "The discovery shape of one dataset: description + declared JSON Schema + size signals, deliberately WITHOUT the payload. This is what an agent reads to judge reuse before authoring something new.",
  key: "The dataset's stable lower-kebab key — what rule code passes to sdk.getDataset(key). Agent-chosen at authoring time, never renamed once rules ship against it.",
  namespace:
    "Coarse lower-kebab grouping for list filtering only; carries no schema semantics.",
  description:
    "What the dataset holds and how rules use it — the text an agent reads to judge whether an existing dataset already covers a need.",
  jsonSchema:
    "The dataset's declared shape, as a JSON Schema document. Writes are validated against it; it is stored data, not compiled code.",
  revision:
    "The dataset's revision (1-based, bumped by every write). An etag, not history: superseded payloads are not kept. Pass it back as `ifRevision` to refuse a write that would overwrite someone else's edit.",
  entryCount:
    "Entries in the current payload (array length or object key count) — a size signal for discovery, computed at write time.",
  payloadBytes:
    "Serialized size of the current payload in bytes (capped at 1 MB at write).",
  lastUpdateTs: "When the dataset was last written, epoch milliseconds.",
} as const;

export const AgenticLogicDatasetDtoDescriptions = {
  ...AgenticLogicDatasetSummaryDtoDescriptions,
  _schema:
    "One dataset as returned by the internal CRUD endpoints: the discovery summary plus the current payload.",
  payload: "The current payload, verbatim.",
} as const;

export const AgenticLogicDependentRuleDescriptions = {
  _schema:
    "One published rule that reads a dataset or Source. While any exists, it cannot be deleted (and a dataset's schema cannot change incompatibly).",
  ruleId: "The rule's id.",
  ruleName: "The rule's display name.",
} as const;

export const AgenticLogicDatasetListItemDtoDescriptions = {
  ...AgenticLogicDatasetSummaryDtoDescriptions,
  _schema:
    "One row of the dataset listing: the discovery summary plus the published rules that read the dataset.",
  usedBy:
    "Published rules whose code reads this dataset. Empty means no published rule depends on it, so it can be deleted.",
} as const;

export const AgenticLogicDatasetReadDescriptions = {
  _schema:
    "What a runtime dataset read returns: the payload plus the revision that produced it, so the evaluation's telemetry can record what actually ran.",
  key: "The dataset key that was read.",
  revision: "The dataset's revision at read time.",
  version:
    "DEPRECATED alias of `revision`, sent by the serving read for rules published before the rename. Read `revision`.",
  payload: "The payload, as a detached plain snapshot.",
} as const;

export const AgenticLogicDatasetRevisionStampDescriptions = {
  _schema:
    "One resolved dataset pin of an evaluation: the key and the exact revision the evaluation read.",
  key: "The dataset key the evaluation read.",
  revision: "The revision the evaluation's pinned read resolved to.",
} as const;

// ---------------------------------------------------------------------------
// Sources — named, input-independent read definitions behind sdk.getSource
// ---------------------------------------------------------------------------

export const AgenticLogicSourceDefinitionDescriptions = {
  _schema:
    "The SDK read a Source names, restricted to reads whose answer does not depend on the request: best sellers, new arrivals, in stock, one collection page, a stored GraphQL query (storefrontGraphql: `$country: CountryCode!` + `@inContext(country: $country)`, country never stored; adminGraphql: catalog/content root fields only, no customer, order or cost fields), or a script (one `source.ts` exporting `async function build(sdk)` that the platform runs on a schedule in a sandbox; its JSON return value, up to 1 MiB, is the Source's answer, read with sdk.getSourceRaw). A GraphQL Source stores ALL of its variables. A typed or GraphQL Source's answer shares the read-cache entry of the equivalent direct SDK read; a script's value has its own entry.",
} as const;

export const AgenticLogicSourceDtoDescriptions = {
  _schema:
    "One Source: a named, org-scoped read definition whose answer the platform keeps cached and refreshed. Rules read it with sdk.getSource(name), or sdk.getSourceRaw(name) for a GraphQL or script Source.",
  name: "The Source's stable lower-kebab name — what rule code passes to sdk.getSource(name) (sdk.getSourceRaw(name) for a GraphQL Source). Never renamed once rules ship against it.",
  description:
    "What the Source reads and what rules use it for — the text an agent reads to judge reuse.",
  definition: "The typed read the Source names.",
  summary: "One human-readable line describing the read.",
  freshMs:
    "How long a fetched answer counts as fresh, in ms: the override when set, else the read kind's default. The scheduled refresh runs at about 80% of it.",
  freshMsOverride:
    "The Source's own freshness in ms, shorter or longer than the read kind's default (1 minute to 24 hours for a read; 15 minutes to 7 days for a script); null when the Source follows the default.",
  status:
    "warming (never fetched), ready (fresh), stale (older than freshMs, still served while it refreshes) or error (the last refresh failed).",
  fetchedAt:
    "When the cached answer was fetched, epoch ms; null when nothing is cached.",
  lastRefreshedAt:
    "When the last scheduled or requested refresh finished, epoch ms; null when none has run.",
  lastRefreshStatus:
    "Outcome of the last scheduled or requested refresh; null when none has run.",
  lastError: "Why the last refresh failed; null after a successful refresh.",
  lastBuildLog:
    "The console output of a script Source's last build (its tail, at most 8 KiB), whether it succeeded or failed; null for other kinds and before the first build.",
  lastReadAt:
    "When a rule last read the Source, epoch ms (recorded at most every 10 minutes); null when never read. Sources idle for 7 days stop refreshing.",
  lastUpdateTs: "When the Source definition was last written, epoch ms.",
} as const;

export const AgenticLogicSourceListItemDtoDescriptions = {
  ...AgenticLogicSourceDtoDescriptions,
  _schema:
    "One row of the Source listing: the Source plus the published rules that read it.",
  usedBy:
    "Published rules whose code reads this Source. Empty means it can be deleted.",
} as const;

export const AgenticLogicSourceReadDescriptions = {
  _schema:
    "What a runtime Source read returns: the definition the name resolved to and the answer of the read it names.",
  name: "The Source name that was read.",
  definition: "The definition the name resolved to at read time.",
  response:
    "The answer of the named read, exactly as the equivalent direct SDK read's port returns it.",
} as const;

// ---------------------------------------------------------------------------
// Rule lifecycle + admin CRUD contract
// (src/functions/agentic-logic/lifecycle-actions/ and the sibling <op>/
// folders). These
// describe the ADMIN surface — rule rows, versions, publish jobs, placements,
// assignments, and org exclusions — not the serving envelope above. Timestamps
// on these DTOs are Unix epoch-ms numbers (they mirror database rows), unlike
// the serving contract's ISO-8601 strings.
// ---------------------------------------------------------------------------

/**
 * The recommendation settings — a rule-level object, not a container one.
 */
export const AgenticLogicRuleSettingsDescriptions = {
  _schema:
    "How the rule behaves around the products it picks: how many it returns, which products it refuses to return, and what the slot does when it returns none. A property of the RULE and not of the location it fills, so 'why is this product not showing' has exactly one answer and the rule graph can state the whole behaviour. Frozen onto each published version — an edit takes effect at the next publish, never mid-flight.",
  maxItems:
    "Most products the rule may return, 1-10. The engine supplies it to the rule as `context.slotsNeeded` and trims the envelope to it. One bound for every surface, because one rule can serve several. A surface may still show fewer at once: the checkout block's grid paginates the returned list into a carousel, which is presentation and stays with the placement.",
  excludeOutOfStock:
    "Drop products with no purchasable variant. Applied where the product data already is: inside the template for a builder rule, inside the generated source for a code rule, and again as a floor when the engine hydrates the result — so the setting is true on every surface rather than only the ones that remembered to pass it.",
  excludeItemsInCart:
    "Drop products the shopper already has in the cart. Runs before the emptiness test, so an envelope this empties falls through to `onEmpty` rather than reporting itself as a result.",
  onEmpty:
    "What the slot does when the rule returns zero items: 'hide' the placement or fall back to 'smart-recs'. Errors and timeouts take the same route as a miss — there is no separate error policy — and stay distinct in telemetry.",
} as const;

export const AgenticLogicRuleDtoDescriptions = {
  _schema:
    "One recommendation rule: the org-scoped container that owns drafts, immutable versions, and placement assignments. The rule row carries lifecycle state; only 'active' rules are evaluable.",
  id: "Server-issued rule id (uuid).",
  organizationId: "Owning organization id (uuid).",
  name: "Merchant-facing rule name.",
  description:
    "Plain-language summary of what the rule recommends, in what order, and for which shoppers.",
  managedBy:
    "'builder' = configured from a template (templateId + templateParams + templateVersion); 'code' = a custom-code workspace rule.",
  state:
    "Lifecycle state: draft → active → paused. Paused renders as rule-absent on serving. Retiring a rule is a hard delete, legal from any state but never while referenced.",
  templateId: "Template identifier for builder rules; null for code rules.",
  templateParams:
    "Template parameters for builder rules, validated against the template's params schema at write time; null for code rules.",
  templateVersion:
    "Template artifact version the rule was configured against (echo-on-execute model); null for code rules.",
  targetSurfaces:
    "Surfaces a code rule declares it is written for, fixed at creation and never editable; the rule may only be assigned to placements on these surfaces. Null for builder rules, whose eligibility comes from their template instead. Declared, never inferred: it states which request context the rule was written against, since a surface cannot supply data it never has (post-purchase has no cart, onsite has no customer).",
  settings:
    "The rule's recommendation settings — item cap, the two product exclusions, and the empty-result policy. The EDITABLE copy: serving reads the copy frozen onto the active version, so changing this changes nothing until the rule is republished.",
  authoringSessionId:
    "Id of the AI chat session that most recently authored this rule, or null for a rule created outside a chat (and for every rule created before the association was recorded). Follows the latest authoring conversation: creating the rule sets it, and a later revision session repoints it, so the transcript the workspace loads back after a reload — including the diagnosis of a build that gave up — is the one that explains the rule's current state.",
  systemKey:
    "Set only on a built-in rule Intelligems provisions for every store (e.g. 'shopify-recommendations'); null or absent on every rule a merchant or Atlas created. A built-in rule is read-only: it cannot be renamed, edited, paused, resumed or deleted, and there is at most one per key per store.",
  createdAtTs: "Creation time, Unix epoch-ms.",
  lastUpdateTs: "Last update time, Unix epoch-ms.",
} as const;

export const EnsureAgenticLogicSystemRuleDescriptions = {
  systemKey:
    "Which built-in rule to ensure. The call is idempotent: the first call for a store creates the rule and enqueues its first publish, and every later call returns the same rule.",
  rule: "The store's built-in rule for this key.",
  latestPublishJob:
    "The rule's most recent publish job, or null when none has been recorded. While the rule is still `draft` this is what says whether its first version is on the way or failed.",
} as const;

export const AgenticLogicRuleDependencyDescriptions = {
  _schema:
    "One npm dependency recorded on a rule draft. Read-only history: Agentic Logic admits no third-party package, so a request that declares one is refused.",
  name: "npm package name.",
  version: "Requested semver range.",
} as const;

export const AgenticLogicRuleDraftDtoDescriptions = {
  _schema:
    "The rule's single mutable draft (exactly one per rule, overwritten on save). Source and user tests live in S3; dependencies live on the row.",
  ruleId: "Owning rule id (uuid).",
  source: "Draft rule source code, or null when nothing has been saved yet.",
  userTestSuite:
    "User-authored test suite saved with the draft, or null when none exists.",
  generatedTestSuite:
    "The AI-generated test suite carried with the draft, or null when none has been generated yet. Reused across publishes and dry runs: the publish pipeline regenerates it only when the rule's inputs or contract surface have moved, and writes the result back here.",
  dependencies:
    "Always empty for a draft saved under the current contract. Agentic Logic rules run with no third-party npm packages — rules read commerce state through the injected SDK — so the field only ever carries what an older draft stored.",
  updatedByUserId: "User who last saved the draft, or null.",
  updatedByUser:
    "Display summary for the user who last saved the draft, or null when the user is not known.",
  lastUpdateTs: "Last save time, Unix epoch-ms.",
  origin:
    "How the draft source came to be: 'chat' when applied from the workspace AI chat and untouched since, 'manual' when a person authored or last edited it, null for drafts saved before provenance was recorded.",
  graph:
    "The rule-graph artifact saved alongside the draft source, or null when none has been emitted yet (pre-graph drafts, human-authored code). Persisted only after save-time validation against the submitted code.",
  contentFingerprint:
    "SHA-256 identity of the code workspace (source, user tests, generated tests). Equal draft and active-version fingerprints mean the draft has nothing to apply. Null for template rules, and for rows not yet fingerprinted.",
} as const;

export const AgenticLogicRuleVersionDtoDescriptions = {
  _schema:
    "One immutable published version of a rule. Exactly one version per rule carries status 'active' (the version pointer); rollback moves the pointer, never rewrites rows.",
  id: "Version row id (uuid).",
  version:
    "Monotonic publish number (1, 2, 3, …). Defines recency for retention.",
  status:
    "'active' = the version the rule serves; 'superseded' = previously active; 'failed' = the publish never promoted it.",
  notes: "Publish notes, when provided.",
  title:
    'Display title for the version (e.g. "Built from chat"). Explicit publish-time title when given, defaulted server-side to "Built from chat" for chat-origin drafts, else null.',
  origin:
    "Provenance copied from the draft at publish time: 'chat' = built by the workspace AI chat, 'manual' = human-authored, null for versions published before provenance was recorded.",
  settings:
    "The recommendation settings frozen with this version. THE copy serving obeys — the rule row's is the editable draft of it — which is what makes a settings edit take effect at republish rather than instantly.",
  templateId:
    "Template this version was built from, or null for a custom-code version. Frozen at publish time: repointing the rule at another template never rewrites it.",
  templateVersion:
    "The template's own version at publish time, or null for a custom-code version. Says which template implementation the frozen params were validated against.",
  templateParams:
    "The normalized template parameters frozen with this version, or null for a custom-code version. THE copy serving executes — the rule row's is the editable draft of them — which is why a params edit takes effect at republish rather than instantly, and why two versions of one rule are only distinguishable here.",
  dependencyLockHash:
    "Hash of the resolved dependency lockfile, or null when the version has no locked dependencies.",
  contentFingerprint:
    "SHA-256 identity of the code workspace (source, user tests, generated tests). Equal draft and active-version fingerprints mean the draft has nothing to apply. Null for template rules, and for rows not yet fingerprinted.",
  shopifyApiVersion:
    "The Shopify API version this version's queries were validated against, captured at publish time. Immutable: bumping the app's version never rewrites it, so the rule keeps serving under this pin until it is republished. Null for versions published before pins were recorded.",
  publishedAtTs: "Promotion time, Unix epoch-ms; null for failed versions.",
  publishedByUserId: "User who published, or null (system publishes).",
  publishedByUser:
    "Display record for the publisher, resolved at read time, or null when there is no publisher or the account no longer resolves. Readers render this; `publishedByUserId` is an identifier, never a label.",
  createdAtTs: "Row creation time, Unix epoch-ms.",
} as const;

export const AgenticLogicRuleVersionDetailDtoDescriptions = {
  _schema: "Version list row plus the artifacts it owns.",
  version: "The version row.",
  sourceUrl:
    "Presigned URL for the version's source snapshot, or null when the version stores none.",
  userTestsUrl:
    "Presigned URL for the user test suite snapshot, or null when none was saved.",
  generatedTestsUrl:
    "Presigned URL for the generated test suite snapshot, or null when none was saved.",
  dependencyLock:
    "Resolved lockfile: package name → exact resolved version. Empty object when the version has no dependencies.",
  graph:
    "The rule-graph artifact the graph_consistency gate validated for this version (custom rules) or regenerated from template params at publish time. Null for versions published before graphs existed. Renderer-ready: no geometry, layout is client-side.",
} as const;

export const RuleGraphNodeDescriptions = {
  _schema:
    "One node in a rule graph. For kind 'branch' the id IS the attempt() base label lexed from the rule's code — that identity is what the graph_consistency gate and the branchFired telemetry join on.",
  id: "Stable node id, unique within the graph. For kind 'branch', MUST equal the attempt() base label (a static string literal in the code). Runtime attempt instances are drill-in data on the node, never nodes of their own.",
  kind: "'input' | 'branch' | 'generator' | 'filter' | 'sort' | 'limit' | 'exclusions' | 'output' | 'fallback-terminal'. Branch nodes mirror attempt() bases; pipeline kinds describe the flow inside or after a branch, while Exclusions is the platform-owned final filter.",
  label: "Short card title shown on the node.",
  summary: "One plain-language sentence describing what the node does.",
  branchId:
    "Owning branch node id, set ONLY on generator/filter/sort/limit nodes that run inside that branch; absent for shared pipeline nodes and always absent on 'input', 'branch', 'exclusions', 'output' and 'fallback-terminal' nodes — a branch never names itself.",
  meta: "Kind-specific structured details (e.g. generator source, aggregate itemCount + sampleItems, limit N). Aggregates belong here — never one node per item.",
} as const;

export const RuleGraphNodeMetaDescriptions = {
  _schema:
    "Typed node meta the renderer understands. Loose: unknown keys ride through untouched.",
  itemCount:
    "Aggregate count of items the node covers (e.g. a generator drawing from a 1000-product list).",
  sampleItems:
    "Up to five representative item names for drill-in display; never the full list.",
  condition:
    "Plain-language matching clause for branch/filter nodes, phrased for a merchant ('in stock right now', 'cart contains a tagged cover-up'). The MATCHING card body.",
  conditionSubject:
    "Which request context a branch node's condition chip is about — 'cart' (the default when absent), 'delivery' (request.shipping.delivery: selected option, local pickup, cost, carrier, transit days), 'shipping' (gap, threshold, tiers, state), 'customer' (ordersCount, tags), 'page' (request.pageProduct), 'order' (request.order), 'time' (request.evaluatedAt). Drives the row-header sentence; absent on always-matching branches and on every non-branch node.",
  sortKey:
    "Plain-language sort criterion for sort nodes ('Best sellers'). The SORTED BY card body.",
  limit:
    "Maximum number of items a limit node lets through. Rendered as 'UP TO n products'.",
} as const;

export const RuleGraphEdgeDescriptions = {
  _schema: "One directed edge in a rule graph.",
  from: "Source node id.",
  to: "Target node id.",
  kind: "'flow' = data/control flow; 'fallback' = taken when the source produced nothing (branch miss or empty output). Fallback edges may only terminate at a branch or the fallback-terminal.",
} as const;

export const RuleGraphDtoDescriptions = {
  _schema:
    "The rule's graph artifact: a renderer-agnostic node/edge description of the rule's selection logic, versioned alongside the code. Carries NO geometry — layout is computed client-side. Produced deterministically for template rules and emitted by the authoring agent for custom-code rules, then verified by the graph_consistency gate.",
  version: "Graph wire-format version. Currently 1.",
  nodes:
    "Graph nodes, at most RULE_GRAPH_MAX_NODES. Large inputs must be aggregated into node meta, never expanded per item.",
  edges: "Directed edges between node ids, at most RULE_GRAPH_MAX_EDGES.",
} as const;

export const RuleGraphCoverageDescriptions = {
  _schema:
    "The join between one evaluation's branch-level telemetry and the rule's graph artifact: which branch fired, the state of every branch, and the solid path the renderer keeps undimmed.",
  firedBranchId:
    "The branch node id the evaluation's branchFired telemetry names, or null when nothing matched (or the reported base names no branch node in this graph revision).",
  branchStates:
    "Per branch node id: 'fired' | 'attempted' (tried but did not fire) | 'skipped' (never reached).",
  traversedNodeIds:
    "Node ids on the evaluation's solid path; the renderer dims everything else.",
  terminalNodeId:
    "The 'output' node when the envelope carries items, the 'fallback-terminal' when it is empty.",
} as const;

export const AgenticLogicPublishGateResultDtoDescriptions = {
  _schema:
    "One gate's result within a publish job. The gate-key vocabulary is owned by the publish-gate registry; the status vocabulary is identical to the custom-shipping-provider pipeline's so workers port cleanly.",
  gateKey: "Which gate this result belongs to.",
  status:
    "'pending' | 'running' | 'passed' | 'failed' | 'warning' | 'blocked'. 'warning' passes with caveats; 'blocked' means an earlier gate's failure prevented this one from running.",
  result: "Human-readable gate summary, when available.",
  detailsJson: "Structured gate details, when available.",
  startedAtTs: "Gate start time, Unix epoch-ms, when started.",
  finishedAtTs: "Gate finish time, Unix epoch-ms, when finished.",
} as const;

export const AgenticLogicRulePublishJobDtoDescriptions = {
  _schema:
    "One publish job: the async unit that runs the gate pipeline against a frozen draft snapshot and, in publish mode, promotes a new active version on success.",
  id: "Publish-job id (uuid) — the handle returned by the publish endpoint.",
  ruleId: "Rule the job publishes (uuid).",
  status: "'queued' | 'running' | 'succeeded' | 'failed'.",
  mode: "'publish' (runs every gate and promotes a version on success) | 'dry_run' (validation gates only, never promotes). What the job IS — a reader cannot infer it from status while the job is still in flight.",
  startedAtTs: "Worker start time, Unix epoch-ms; null while queued.",
  finishedAtTs: "Terminal time, Unix epoch-ms; null until the job finishes.",
  resultingVersionId:
    "Version the job promoted, when it succeeded in publish mode; null otherwise (and null again if that version is later deleted by retention).",
  gates: "Per-gate results, in pipeline order.",
} as const;

export const AgenticLogicPlacementDtoDescriptions = {
  _schema:
    "One placement: a server-issued addressable location a surface renders recommendations into. The placement's id IS the `placementId` requests and impressions carry.",
  id: "Server-issued placement id (uuid). Wire identity — never renamed or reused.",
  organizationId: "Owning organization id (uuid).",
  name: "Merchant-facing placement name.",
  surface:
    "Surface the placement renders on ('onsite' | 'checkout' | 'post-purchase'). Fixed at creation — a new surface is a new placement.",
  createdAtTs: "Creation time, Unix epoch-ms.",
  lastUpdateTs: "Last update time, Unix epoch-ms.",
  lastSeenAtTs:
    "Liveness: when a real shopper request for this placement last reached the engine (Unix epoch-ms), or null if none has within the retention window. Only the `serving` lane beats it — previews and API evaluations never do — so it answers 'is this placement actually live on the storefront', not 'did it render products'. Best-effort and TTL-bounded; never a billing or analytics source.",
} as const;

export const AgenticLogicPlacementAssignmentDtoDescriptions = {
  _schema:
    "One rule ↔ placement assignment — a cell of the (variation × placement) matrix. Carries which rule fills the cell; the evaluation budget belongs to the assignment and the empty-result policy to the rule. One rule can be assigned to many placements.",
  id: "Assignment id (uuid).",
  placementId: "Placement being filled (uuid).",
  ruleId: "Rule that fills it (uuid).",
  variationId:
    "Experiment variation this assignment belongs to, or null for the org's global default. The global cell applies to EVERY variation unless that variation has an enabled cell of its own, which overrides it. Only the experience save writes a variation cell; the assignment verbs write global rows only.",
  experience:
    "The experience whose variation this assignment belongs to, resolved for display. Null on a global assignment, and also null when the variation no longer resolves — variationId is deliberately not a foreign key.",
  budgetMs:
    "Per-assignment evaluation budget in milliseconds (maximum 5000ms), or null to use the runtime default. Publish gates check rules against the tightest budget across their enabled assignments.",
  enabled:
    "Per-assignment pause. False = the cell is skipped at serve time without touching the rule's lifecycle state — a disabled variation cell therefore falls back to the placement's global cell, and only serves nothing when the global cell is missing or disabled too.",
  createdAtTs: "Creation time, Unix epoch-ms.",
  lastUpdateTs: "Last update time, Unix epoch-ms.",
} as const;

export const AgenticLogicOrgExclusionsDtoDescriptions = {
  _schema:
    "The org's serve-time ban list, enforced by the broker on every result envelope. One row per org; edits are effective immediately (no republish). All ids are Shopify GIDs.",
  productIds: "Products never to recommend (gid://shopify/Product/<n>).",
  tags: "Product tags never to recommend.",
  collections:
    "Collections whose members are never recommended (gid://shopify/Collection/<n>).",
  vendors: "Vendors whose products are never recommended.",
  lastUpdateTs:
    "Last edit time, Unix epoch-ms; null when the org has never saved exclusions.",
} as const;

export const CopyAgenticLogicRuleRequestDescriptions = {
  _schema:
    "Fork a code rule's current workspace into a rule of its own. The copy is a rule like any other: its first save is its first published version.",
  name: "Merchant-facing name for the copy.",
  description: "Plain-language summary for the copy.",
  draft:
    "Current workspace to save on the copy. Omit it to copy the source rule's persisted draft.",
  discardSourceDraft:
    "When true, reset the source rule to its live version in the same transaction that creates the copy.",
} as const;

export const CreateAgenticLogicRuleRequestDescriptions = {
  _schema: "Create a rule container (state starts at 'draft').",
  name: "Merchant-facing rule name.",
  description:
    "Plain-language summary of what the rule recommends, in what order, and for which shoppers.",
  managedBy: "'builder' or 'code'. Immutable after creation.",
  templateId:
    "Template id for builder rules. Required when managedBy is 'builder'.",
  templateParams:
    "Template params for builder rules, validated against the template's params schema at write time.",
  templateVersion:
    "Template artifact version the params were authored against.",
  settings:
    "The rule's recommendation settings. Optional: omit to take the defaults (3 items, both exclusions on, hide when empty), which is what a merchant who never mentions them should get.",
  targetSurfaces:
    "Surfaces this code rule is written for, and the only surfaces whose placements it may later be assigned to. Required for code rules, refused for builder rules, and immutable once set. Choose from the surfaces that can supply the request context the rule reads: post-purchase has an order and no cart, onsite has no customer identity, checkout has no page product.",
  authoringSessionId:
    "Id of the AI chat session authoring this rule. Optional: a rule created outside a chat omits it. A later authoring session repoints it, so the workspace always loads back the conversation that explains the rule's current state.",
} as const;

export const AgenticLogicTemplateSummaryDescriptions = {
  _schema:
    "Authoring metadata for one trusted Agentic Logic template in the admin template catalog.",
  id: "Stable template id used by builder-managed rules.",
  title: "Short administrator-facing template name.",
  description: "Plain-language summary of the template behavior.",
  behavior:
    "Authoritative statements of what this template does at serve time — ordering, filtering, slot fill, and what it cannot do. First-party text: the only permitted source for any claim about this template's behavior.",
  eligibleSurfaces: "Agentic Logic surfaces on which the template can run.",
  templateVersion: "Current live positive integer template version.",
  exampleParams:
    "Valid example configuration used to seed the JSON template editor.",
  paramsSchema:
    "JSON Schema (draft 2020-12) for this template's parameters, derived from the live template's own validator. The complete and only set of accepted keys: any capability the behavior text claims is expressible here, and a key absent here is rejected.",
  intake:
    "The template's opening questions, in order: the structured intake an authoring agent asks as ONE request-user-input survey when the merchant has only selected the template. Each question names the parameter its answer settles. Empty when the template has no chip-shaped parameters and the agent must ask in prose.",
} as const;

export const AgenticLogicTemplateIntakeChoiceQuestionDescriptions = {
  type: "Always 'choice': the answer is one of this parameter's accepted values.",
  param:
    "The top-level parameter key (a property of paramsSchema) the chosen option's value is written to.",
  prompt: "The question text shown while this question is active.",
  options:
    "Two to five options. Each 'value' is a literal accepted value of the parameter, 'label' is what the merchant reads, and a 'description' of 'Default' marks the value the template uses when the parameter is omitted.",
} as const;

export const AgenticLogicTemplateIntakeShopifyPickQuestionDescriptions = {
  type: "Always 'shopify-pick': the answer comes from the merchant's Shopify catalog through the surface's picker.",
  param:
    "The top-level parameter key (a property of paramsSchema) the picked identifiers are written to — as an array for a multiple pick, as the single identifier otherwise.",
  prompt: "The question text shown while this pick is active.",
  entity:
    "What the merchant picks: 'product', 'collection', 'tag', 'vendor' or 'product_type'.",
  selection:
    "'multiple' when the parameter is a list of catalog identifiers, 'single' when it is exactly one.",
} as const;

export const ListAgenticLogicTemplatesResponseDescriptions = {
  _schema: "The trusted Agentic Logic template authoring catalog.",
  templates: "Registered templates in their curated display order.",
} as const;

export const ValidateAgenticLogicTemplateRequestDescriptions = {
  _schema:
    "Validate and normalize a JSON configuration against one trusted template.",
  templateId: "Stable template id to validate against.",
  templateParams: "JSON-object configuration supplied by the administrator.",
  settings:
    "The rule's recommendation settings, used only to draw the derived graph honestly — the item cap on the UP TO card, the exclusions as filter cards, and the empty-result policy on the fallback terminal. Omit to draw against the defaults.",
} as const;

export const ValidateAgenticLogicTemplateIssueDescriptions = {
  _schema: "One template configuration validation issue.",
  path: "Dot-separated path to the invalid configuration value.",
  message: "Human-readable reason the value is invalid.",
} as const;

export const ValidateAgenticLogicTemplateResponseDescriptions = {
  _schema:
    "The normalized template configuration or structured validation issues.",
} as const;

export const GetAgenticLogicTemplateSourceRequestDescriptions = {
  _schema:
    "Read the first-party implementation source of one trusted template.",
  templateId: "Stable template id whose implementation source to read.",
} as const;

export const AgenticLogicTemplateSourceFileDescriptions = {
  _schema: "One TypeScript source file of a template implementation.",
  path: "File path relative to the template library root.",
  contents: "Verbatim TypeScript source text.",
} as const;

export const GetAgenticLogicTemplateSourceResponseDescriptions = {
  _schema:
    "The read-only implementation source that runs for rules configured against this template.",
  templateVersion: "Live template version the source snapshot was taken from.",
  files:
    "Template entry file first, then its transitive template-library imports in path order.",
} as const;

export const UpdateAgenticLogicRuleRequestDescriptions = {
  _schema:
    "Update a rule's mutable fields. Lifecycle state moves through the lifecycle verbs, never through this endpoint.",
  name: "New merchant-facing name.",
  description: "New plain-language rule summary.",
  settings:
    "New recommendation settings. Replaces the stored object whole — a partial object is not merged. Takes effect at the next publish.",
  templateParams:
    "New template params for builder rules, validated against the template's params schema at write time.",
  templateVersion:
    "Template artifact version the new params were authored against.",
  authoringSessionId:
    "Id of the AI chat session that authored this rule, recorded by the workspace client once the session id is known (the create call is made by a tool that does not carry it). Re-sending the stored id is a no-op; a different id repoints the rule at that newer conversation, which is what a revision session does when it finishes.",
} as const;

export const SaveAgenticLogicRuleDraftRequestDescriptions = {
  _schema:
    "Overwrite the rule's single draft. Publishing snapshots the draft at job creation, so later saves never change an in-flight publish.",
  source: "Draft rule source code.",
  userTestSuite:
    "User-authored test suite to persist with the draft. Omit to leave the existing suite untouched; send an empty string to clear it.",
  generatedTestSuite:
    "Edits to the AI-generated test suite. Omit to leave the stored suite untouched; send an empty string to clear it and let the next publish generate a fresh one. An explicit suite is stored as hand-authored, so the next publish runs it to decide whether it still covers the rule rather than assuming either way.",
  dependencies:
    "Must be empty. Agentic Logic rules run with no third-party npm packages: the rule runtime's no-network, no-clock boundary is scoped to the rule's own files, and a bundled package would run inside the rule with that boundary drawn around nothing. Read commerce state through the injected SDK instead.",
  validationScenarios:
    "Request-side scenarios the publish gates evaluate the rule against. Omit to leave the draft's existing scenarios untouched; send an empty array to discard them. Publishing requires at least one. There is no read-side half to save: gate-time SDK reads are served live, and the scan-cost report is derived from the validation run itself rather than persisted here.",
  graph:
    "The rule-graph artifact emitted WITH this source. Validated at save time against the submitted code (branch node ids must equal the attempt() base labels; structure must be a capped DAG) and rejected with mismatch details on disagreement. Omit to clear any previously saved graph — a graph never outlives the source it described.",
  origin:
    "Provenance of the submitted source: 'chat' when the editor buffer was applied from the workspace AI chat and untouched since, 'manual' when a person authored or last edited it. Omit to leave the stored provenance untouched.",
} as const;

export const StaffSaveAgenticLogicRuleDraftRequestDescriptions = {
  ...SaveAgenticLogicRuleDraftRequestDescriptions,
  autoAssist:
    "Whether the save starts AI assistance. Defaults to true. False stores the draft without starting AI graph or scenario regeneration, the initial dry run, or a repair loop; a repair loop already running is not stopped. Applies to this call only.",
} as const;

export const PublishAgenticLogicRuleRequestDescriptions = {
  _schema:
    "Start an async publish job for the rule's current draft. Returns the job handle to poll.",
  notes: "Publish notes to stamp on the resulting version.",
  title:
    'Display title to stamp on the resulting version. When omitted, chat-origin drafts default to "Built from chat"; manual drafts get no title.',
  idempotencyKey:
    "Client-supplied idempotency key. A repeated publish for the same rule and key returns the existing job handle instead of starting another job.",
} as const;

export const StaffPublishAgenticLogicRuleRequestDescriptions = {
  ...PublishAgenticLogicRuleRequestDescriptions,
  autoAssist:
    "Whether a failed job starts AI repair. Defaults to true. False leaves a failed job failed: this job opens no repair loop, so it never leads the platform to rewrite or republish the rule's source. A repair loop already running for the rule is not stopped. A successful publish is unaffected. A replay of an existing idempotencyKey returns that job as it was started, whatever this says.",
} as const;

export const StaffEvaluateAgenticLogicRuleScenarioResponseDescriptions = {
  _schema: "The scenario preview, plus the logger output of a code rule's run.",
  logs: "Entries the rule's `logger` calls wrote during this run, in call order. Present for code rules, including when the rule threw or exceeded its budget; absent for builder rules and when the run failed on the platform side (for example a failed SDK read), including a throw after the rule caught a failed SDK read; a budget overrun after one still has them. When a failed run's output exceeds 256KB, only its earliest and latest entries are kept and `logsTruncated` is true. Each `timestamp` is host wall-clock time when `logger` was called, with millisecond precision, so the difference between two entries is the elapsed time between those calls.",
  logsTruncated:
    "True when the failed run wrote more than 256KB of output, so `logs` holds only its earliest and latest entries. Absent when `logs` is complete.",
} as const;

export const ExternalAgenticLogicRuleActionRequestDescriptions = {
  _schema:
    "Lifecycle action body for the external actions route. `rollback` requires `version`; every other action rejects it.",
  version:
    "Publish number of the version to activate. Required for `rollback`, rejected for the other actions.",
  activeVersionId:
    "Id of the version row now serving as active. Present after `rollback` only.",
} as const;

export const RunAgenticLogicRuleGatesRequestDescriptions = {
  _schema:
    "Start an async dry-run job that evaluates the validation gates against the rule's current draft without publishing a version. Returns the job handle to poll.",
  idempotencyKey:
    "Client-supplied idempotency key. A repeated request for the same rule and key returns the existing job handle instead of starting another job; keys are shared with publish.",
} as const;

export const StaffRunAgenticLogicRuleGatesRequestDescriptions = {
  ...RunAgenticLogicRuleGatesRequestDescriptions,
  autoAssist:
    "Whether a failed dry run starts AI repair. Defaults to true. False leaves a failed job failed: this job opens no repair loop, so it never leads the platform to rewrite or republish the rule's source. A repair loop already running for the rule is not stopped. A replay of an existing idempotencyKey returns that job as it was started, whatever this says.",
} as const;

export const RollbackAgenticLogicRuleRequestDescriptions = {
  _schema:
    "Move the active-version pointer to an existing older version. The rule stays active; no rows are rewritten.",
  version: "Publish number of the version to activate.",
} as const;

export const CreateAgenticLogicPlacementRequestDescriptions = {
  _schema:
    "Create a placement. The response's server-issued id is the wire `placementId`.",
  name: "Merchant-facing placement name.",
  surface: "Surface the placement renders on. Fixed at creation.",
} as const;

export const UpdateAgenticLogicPlacementRequestDescriptions = {
  _schema:
    "Edit a placement's name. The id and surface never change, and the empty-result policy belongs to the rule.",
  name: "New merchant-facing name.",
} as const;

export const CreateAgenticLogicPlacementAssignmentRequestDescriptions = {
  _schema:
    "Assign a rule to a placement as the org's GLOBAL default — the cell with no variation, which serves every variation that has no enabled cell of its own. Variation cells are not writable here: they are derived from the experience save, which owns which rule each variation serves. See AgenticLogicPlacementAssignmentDtoDescriptions.variationId.",
  placementId: "Placement being filled (uuid).",
  ruleId: "Rule that fills it (uuid).",
  variationId:
    "Must be null or omitted — this verb writes the GLOBAL cell only. A non-null variation is refused: variation cells are derived from the experience save, which owns which rule each variation serves.",
  budgetMs:
    "Per-assignment evaluation budget in ms, up to 5000ms. Omit to use the placement surface's cap, which is what serving enforces.",
  enabled: "Per-assignment pause flag. Defaults to true.",
} as const;

export const UpdateAgenticLogicPlacementAssignmentRequestDescriptions = {
  _schema:
    "Update the rule or serving config for one (placement, variation) assignment cell.",
  ruleId:
    "Rule the existing assignment cell should serve. Repointing keeps the placement, variation, and serving settings intact.",
  budgetMs:
    "New per-assignment budget in ms, up to 5000ms, or null to return to the placement surface's cap.",
  enabled: "Per-assignment pause flag.",
} as const;

export const PutAgenticLogicOrgExclusionsRequestDescriptions = {
  _schema:
    "Full-replace write of the org's exclusions (one JSON row per org — no partial merge). Effective immediately on the next evaluation; no republish.",
  productIds: "Products never to recommend (gid://shopify/Product/<n>).",
  tags: "Product tags never to recommend.",
  collections:
    "Collections whose members are never recommended (gid://shopify/Collection/<n>).",
  vendors: "Vendors whose products are never recommended.",
} as const;

export const PatchAgenticLogicOrgExclusionsRequestDescriptions = {
  _schema:
    "Add or remove selected org exclusions without replacing unrelated values. Effective immediately on the next evaluation; no republish.",
  add: "Exclusion values to add. Existing values are unchanged.",
  remove: "Exclusion values to remove. Missing values are unchanged.",
} as const;

// ---------------------------------------------------------------------------
// Publish-gate + required-test validation vocabulary
// (src/functions/agentic-logic/gates/)
// ---------------------------------------------------------------------------

export const AgenticLogicValidationScenarioDescriptions = {
  _schema:
    "One case the publish gates evaluate a rule against: the request side only. There is no read-side half — gate-time SDK reads are served live through the same broker that serves production traffic, so a scenario pins what is asked of the rule and never what the org answers. Determinism is measured rather than replayed: every scenario is invoked twice and the envelopes must match.",
  name: "Human-readable scenario name, shown in test results and gate details.",
  request:
    "The recommendation request the scenario evaluates. `placementId` and `surface` here are wire shape, not references: gates evaluate the rule directly, never an assignment, so no real placement is involved.",
  context:
    "Optional evaluation context override. When absent the harness supplies a representative preview context.",
  evaluatedAt:
    "Optional author-pinned evaluation instant, epoch milliseconds UTC. When absent the harness stamps the publish job's own instant. Pin it to make a time-dependent scenario stable: a case like 'a 30-day supply ordered 34 days ago' needs both ends fixed, or its expected verdict drifts with the calendar.",
  description:
    "Display copy for scenario pickers, phrased for a merchant (e.g. 'A cart that matches your matching cover-ups rule'). Absent on scenarios authored before display copy existed.",
  targetBranchId:
    "The branch this scenario is designed to fire: the graph branch node id (=== the attempt() base label). Null marks the nothing-matches scenario whose cart clears no branch; absent means unannotated (typically hand-authored).",
  origin:
    "'authored' = written by a person or authoring agent alongside the rule; 'generated' = produced by the scenario-generation service. Regeneration replaces only 'generated' scenarios and never clobbers authored ones.",
} as const;

export const AgenticLogicValidationScenarioInputDescriptions = {
  requestPlacementId:
    "Optional at authoring time; omit it and the server fills the rule's own id when the draft is saved. Wire shape only — publish gates evaluate the rule directly, never a placement assignment, so no real placement is needed and the author should never be asked for one.",
} as const;

export const AgenticLogicTemplateInvocationDescriptions = {
  _schema:
    "What the template runner hands a template: the rule invocation minus `secrets`. Templates are first-party (trusted) code and never receive org-scoped secrets — a deliberate contract omission, not an oversight; a `secrets` key on the wire is stripped, never forwarded.",
  request: AgenticLogicInvocationDescriptions.request,
  candidates: AgenticLogicInvocationDescriptions.candidates,
  context: AgenticLogicInvocationDescriptions.context,
} as const;

export const AgenticLogicTemplateRunnerInputDescriptions = {
  _schema:
    "Direct-invoke payload for the shared warm template-runner Lambda that executes first-party recommendation templates. Same envelope, telemetry, and fallback path as custom rules — only the input side differs per tier.",
  templateId:
    "Registry identifier of the first-party template to execute. The in-repo registry holds exactly ONE live implementation per templateId.",
  templateVersion:
    "The template version the rule was configured against (stored on the rule at configuration time). Informational at execution: the runner ALWAYS executes the registry's current implementation and echoes what actually ran via templateVersionExecuted — a mismatch is telemetry/warning material, never a refusal.",
  params:
    "Template parameters as a plain JSON object — pure data, never code. The runner input schema deliberately does not deep-validate them; the registry's per-template Zod params schema does, both at builder config-write time and again on every invocation.",
  settings:
    "The rule's recommendation settings, frozen with the version being executed. The two exclusions come from HERE and never from `params` — they are the merchant's answer to 'which products do you refuse to show', which is the same question whichever template is running, so a template that declared them as its own parameters would have been the second owner of a setting the rule already owns.",
  invocation:
    "The rule invocation being evaluated (request + context + reserved candidates). Deliberately carries NO secrets — first-party templates never receive org-scoped secrets.",
  system:
    "The trusted per-invocation runtime context the broker mints: the signed SDK capability, the trusted read function name, the seeded-shuffle inputs (visitorId + ruleVersion), the locale/currency the reads run under, and the absolute deadline. The same block the custom-rule tier receives, so both tiers build an identical SDK runtime. Never template-chosen.",
  timeoutBudgetMs:
    "Wall-clock budget for the template execution, in milliseconds, up to AGENTIC_LOGIC_RUNTIME_BUDGET_MAX_MS (5000ms). Optional on the wire; defaults to AGENTIC_LOGIC_TEMPLATE_RUNNER_DEFAULT_BUDGET_MS (1000ms), so the parsed shape always carries it. Exceeding it is a domain error (telemetry outcome 'error', errorClass 'timeout'), never a hung invoke.",
  recordReadOperations:
    "Whether the result carries `readOperations`. A publish's validation runs set it, because the version's read manifest is derived from them; serving leaves it off (the default), so its hot path neither sends nor parses them.",
} as const;

export const AgenticLogicTemplateRunnerResultDescriptions = {
  _schema:
    "What the template runner returns: the same result envelope + evaluation telemetry contract every rule tier produces, plus the executed-version echo. Domain failures (template throw, timeout, invalid params) are captured INTO this result; only infrastructure failures surface as a Lambda FunctionError.",
  envelope:
    "The recommendation result envelope the template produced, or an empty envelope (zero items) when the evaluation failed — errors collapse to empty behaviorally while staying distinct in telemetry.",
  telemetry:
    "Evaluation telemetry for this run: 'result', 'empty', or 'error' with an errorClass.",
  templateVersionExecuted:
    "Version of the template implementation that actually ran — the runtime witness of execute-current + echo semantics. The registry holds one live implementation per templateId; the runner never refuses on a mismatch with the input's templateVersion. Readout change markers drawn from this echo are the compensating control that keeps implementation fixes from shipping invisibly into live experiments.",
  readOperations:
    "Every distinct SDK read this run issued, as the full read operation — present only when the input set `recordReadOperations`. A publish derives the version's read manifest from these.",
} as const;

/**
 * One recorded rule run as the admin "Recent runs" readout shows it: the
 * `al_telemetry` row minus its routing discriminators. Field copy is the
 * telemetry copy verbatim — one vocabulary for the producer and the readout.
 */
const {
  _schema: _telemetrySchemaDescription,
  eventType: _telemetryEventType,
  engine: _telemetryEngine,
  ...AgenticLogicRuleRunDtoFieldDescriptions
} = AgenticLogicTelemetryEventPayloadDescriptions;

export const AgenticLogicRuleRunDtoDescriptions = {
  _schema:
    "One recorded execution of a rule, read back for the admin Recent runs view. The `al_telemetry` row for that evaluation minus its routing discriminators: rule-scoped, so `ruleId` and `invocationId` are always present.",
  ...AgenticLogicRuleRunDtoFieldDescriptions,
} as const;
